(function () {
  function parseJson(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Bundle builder: JSON parse failed', e);
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + '\u00a0€';
  }

  function formatMoneyAmount(cents) {
    const formatted = formatMoney(cents);
    return formatted.replace(/[^\d,.-]/g, '').trim();
  }

  function formatPriceDelta(cents) {
    if (!cents) return '';
    const sign = cents > 0 ? '+' : '-';
    return `${sign}${formatMoneyAmount(Math.abs(cents))}\u00a0€`;
  }

  function generateBundleInstanceId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function generateBundleId(group, instanceId) {
    const bundleGroup = group || 'Rezept';
    const id = instanceId || generateBundleInstanceId();
    return `${bundleGroup}-${id}`;
  }

  function bundleLineProperties(bundleId, parentVariantId, bundleTitle, bundleGroup, instanceId) {
    const props = {
      _BundleId: bundleId,
      _DiscountId: bundleId
    };
    if (bundleGroup) {
      props._BundleGroup = bundleGroup;
    }
    if (instanceId) {
      props._BundleInstance = instanceId;
    }
    if (parentVariantId) {
      props._BundleParentVariantId = String(parentVariantId);
    }
    if (bundleTitle) {
      props._BundleTitle = bundleTitle;
    }
    return props;
  }

  function bundleAddonLineProperties(bundleId, bundleGroup, discountScaleQty, instanceId, bundleTitle) {
    const props = {
      _BundleId: bundleId,
      _DiscountId: bundleId,
      _BundleDiscountScaleQty: String(discountScaleQty)
    };
    if (bundleGroup) {
      props._BundleGroup = bundleGroup;
    }
    if (instanceId) {
      props._BundleInstance = instanceId;
    }
    if (bundleTitle) {
      props._BundleTitle = bundleTitle;
    }
    return props;
  }

  function bundleGiftLineProperties(instanceId, slot) {
    const props = { _BundleGift: '1' };
    if (instanceId) {
      props._BundleInstance = instanceId;
    }
    if (slot) {
      props._BundleGiftSlot = String(slot);
    }
    return props;
  }

  function formatDrawerProductTitle(title) {
    if (!title) return '';
    const parts = title
      .split(' - ')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length > 2) return parts.slice(2).join(' - ');
    return title;
  }

  function getVariantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;
    const options = [];
    if (variant.option1 != null && variant.option1 !== '') options.push(variant.option1);
    if (variant.option2 != null && variant.option2 !== '') options.push(variant.option2);
    if (variant.option3 != null && variant.option3 !== '') options.push(variant.option3);
    return options;
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._drawerProductsRendered = false;

      this.formId = this.getAttribute('data-form-id');
      this.kitOptionIndex = parseInt(this.getAttribute('data-kit-option-index'), 10);
      this.kitOptionPosition = parseInt(this.getAttribute('data-kit-option-position'), 10);
      this.drawerId = this.getAttribute('data-drawer-id');
      this.bundleGroup = this.getAttribute('data-bundle-group') || 'Rezept';
      this.bundleGroupWithGoodie = (this.getAttribute('data-bundle-group-with-goodie') || '').trim();
      this.bundleTitle = this.getAttribute('data-bundle-title') || 'Dein Bundle';
      this.redirectToCheckout = this.getAttribute('data-redirect-to-checkout') === 'true';
      this.drawerShowAllSoldOut =
        this.getAttribute('data-drawer-show-all-sold-out') === 'true';
      this.atcSuccessMessage =
        this.getAttribute('data-atc-success-message') || '🎉 Auswahl komplett!';
      this.useKitIndexAsQuantity =
        this.getAttribute('data-use-kit-index-as-quantity') === 'true';
      this.usePresetSlotQuantity =
        this.getAttribute('data-use-preset-slot-quantity') === 'true';
      this.lockGallery = this.getAttribute('data-lock-gallery') === 'true';
      this.cardsDiscountMode =
        this.getAttribute('data-cards-discount-mode') || 'per_unit';
      this.variantPricingOnly = this.getAttribute('data-variant-pricing-only') === 'true';
      this.omitParentFromCart = this.getAttribute('data-omit-parent-from-cart') === 'true';
      this.giftMode = this.getAttribute('data-gift-mode') || 'standard';
      this.giftOptional = this.getAttribute('data-gift-optional') === 'true';
      this.includeGiftsInBundle = this.getAttribute('data-include-gifts-in-bundle') === 'true';
      this.gift1OptIn = this.getAttribute('data-gift-1-opt-in') === 'true';
      this.gift1AllPresets = this.getAttribute('data-gift-1-all-presets') === 'true';
      this.gift1DrawerTitle =
        this.getAttribute('data-gift-1-drawer-title') || 'Wähle deinen Shaker';
      this.gift1DeclineLabel =
        this.getAttribute('data-gift-1-decline-label') || 'Nein, ich möchte keinen';
      this.gift1AcceptLabel =
        this.getAttribute('data-gift-1-accept-label') || 'Ja, ich möchte einen';
      this.giftDeclineLabel =
        this.getAttribute('data-gift-decline-label') || 'Ich möchte keinen';
      this.giftOptInDeclined = {};
      this.flexibleSelection = this.getAttribute('data-flexible-selection') === 'true';
      this.limitOnePerProduct = this.getAttribute('data-limit-one-per-product') === 'true';
      this.limitOnePerProductNote =
        this.getAttribute('data-limit-one-per-product-note') ||
        'Jedes Rezeptkarten-Set kann nur einmal ausgewählt werden.';
      this.optionalExtraSlot =
        !this.flexibleSelection && this.getAttribute('data-optional-extra-slot') === 'true';
      this.flexibleConfig = this.flexibleSelection
        ? parseJson(this.querySelector('[data-bundle-flexible-config]')) || {}
        : null;
      this._flexibleUpgradeInProgress = false;
      this._flexibleAutoUpgraded = false;
      this._flexibleMaxExtraQty = 99;
      this._optionalMaxExtraQty = 99;

      this.variants = parseJson(this.querySelector('[data-bundle-variants]')) || [];
      this.products = parseJson(this.querySelector('[data-bundle-products]')) || [];
      this.presets = this._normalizePresets(parseJson(this.querySelector('[data-bundle-presets]')) || []);
      this.giftPricing = parseJson(this.querySelector('script[data-bundle-gifts]')) || {
        minQuantity: 4,
        totalCompare: 0,
        items: []
      };
      this.giftSelections = {};
      this.giftPickLabel = this.getAttribute('data-gift-pick-label') || 'Farbe wählen';
      this.colorsPlaceholder =
        this.getAttribute('data-colors-placeholder') || 'Farben auswählen...';
      this._activePickerContext = null;

      this.slotsEl = this.querySelector('[data-bundle-slots]');
      this.slotTemplate = this.querySelector('[data-bundle-slot-template]');
      this.drawer = document.getElementById(this.drawerId);
      this.drawerList = this.drawer?.querySelector('[data-drawer-list]');
      this.drawerConfirm = this.drawer?.querySelector('[data-drawer-confirm]');
      this.drawerConfirmTitle = this.drawer?.querySelector('[data-drawer-confirm-title]');
      this.drawerConfirmCount = this.drawer?.querySelector('[data-drawer-confirm-count]');
      this._drawerConfirmDefaultTitle = this.drawerConfirmTitle?.textContent?.trim() || '';
      this.drawerTotal = this.drawer?.querySelector('[data-drawer-total]');
      this.drawerCompare = this.drawer?.querySelector('[data-drawer-compare]');
      this.drawerDiscountBadge = this.drawer?.querySelector('[data-drawer-discount-badge]');
      this.drawerCelebration = this.drawer?.querySelector('[data-drawer-celebration]');
      this.drawerGoalToast = this.drawer?.querySelector('[data-drawer-goal-toast]');
      this.drawerUpgradeProgress = this.drawer?.querySelector('[data-drawer-upgrade-progress]');
      this.drawerUpgradeProgressText = this.drawer?.querySelector(
        '[data-drawer-upgrade-progress-text]'
      );
      this.drawerUpgradeProgressBadge = this.drawer?.querySelector(
        '[data-drawer-upgrade-progress-badge]'
      );
      this.atcCelebration = this.querySelector('[data-atc-celebration]');
      this.atcGoalToast = this.querySelector('[data-atc-goal-toast]');
      this.atcButton = this.querySelector('[data-bundle-atc]');
      this._drawerLastTotal = 0;
      this._celebrationTimeout = null;
      this._goalToastTimeout = null;
      this._atcCelebrationTimeout = null;
      this._colorsDrawerLastCount = 0;
      this._colorsCelebrationTimeout = null;
      this._missingHighlightTimeout = null;
      this._uniqueLimitNoteTimeout = null;

      this.selections = [];
      this.presetSelections = {};
      this.drawerQuantities = {};
      this._kitSelectInProgress = false;
      this._kitRevertInProgress = false;
      this._suppressVariantChangeHandling = false;
      this._lastGalleryVariant = null;
      this._gallerySyncRequestId = 0;
      this._suppressDrawerPersist = false;
      this.activePreset = null;
      this.defaultUnitPrice = parseInt(this.getAttribute('data-default-unit-price'), 10) || 0;
      this.shopDisplayDiscount = parseInt(this.getAttribute('data-shop-display-discount'), 10) || 0;
      this.enableCustomPresetComparePrices =
        this.getAttribute('data-enable-custom-preset-compare') === 'true';
      this.preselectPresetIndex = parseInt(this.getAttribute('data-preselect-preset-index'), 10) || 0;
      this.autoSelectSingleProduct = this.getAttribute('data-auto-select-single') === 'true';

      this._bindEvents();
      this._configureDrawer();
      this._initGiftDrawer();
      this._initColorsDrawer();
      this._initGiftSelections();
      this._waitForForm();
      this._updateKitRowPricing();
      this._applyInitialDirectOffer();
    }

    _applyInitialDirectOffer() {
      const row = this.querySelector('[data-bundle-kit-row][data-direct-offer].is-selected');
      if (!row) return;
      this._showDirectOfferGallery(row);
      this._syncDirectOfferUi();
    }

    _getDrawerOpenFrom(side = 'right') {
      const query =
        window.themeVariables?.breakpoints?.['sm-max'] || 'screen and (max-width: 699px)';
      return window.matchMedia(query).matches ? 'bottom' : side;
    }

    _configureDrawer() {
      if (!this.drawer) return;
      Object.defineProperty(this.drawer, 'openFrom', {
        get: () => 'right',
        configurable: true
      });
    }

    _waitForForm() {
      let attempts = 0;
      const tryInit = () => {
        if (this.form) {
          this._initFromForm();
          return;
        }
        attempts += 1;
        if (attempts < 60) requestAnimationFrame(tryInit);
      };
      tryInit();
    }

    get form() {
      return (
        document.getElementById(this.formId) ||
        document.forms.namedItem(this.formId) ||
        document.forms[this.formId]
      );
    }

    get variantPicker() {
      return document.querySelector(`variant-picker[form-id="${this.formId}"]`);
    }

    _normalizePresets(presets) {
      const fallbackQtyByPreset = { 1: 4, 2: 8, 3: 1, 4: 4 };
      return presets.map((preset, index) => {
        const presetIndex = parseInt(preset.presetIndex, 10) || index + 1;
        const indexQuantity =
          this.useKitIndexAsQuantity && !this.usePresetSlotQuantity ? presetIndex : null;
        return {
          ...preset,
          presetIndex,
          quantity:
            indexQuantity ||
            parseInt(preset.quantity, 10) ||
            fallbackQtyByPreset[presetIndex] ||
            1,
          freeQuantity: parseInt(preset.freeQuantity, 10) || 0,
          discountPercent: this._parseDiscountPercent(preset.discountPercent),
          customPriceCents: parseInt(preset.customPriceCents, 10) || 0,
          customCompareCents: parseInt(preset.customCompareCents, 10) || 0,
          bundleGroup: preset.bundleGroup || null
        };
      });
    }

    _parseDiscountPercent(value) {
      const parsed = parseFloat(String(value ?? '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : 0;
    }

    _hasOptionalExtraSlots() {
      return this.flexibleSelection || this.optionalExtraSlot;
    }

    _getMaxSelectableQty() {
      if (this.flexibleSelection) return this._getFlexibleMaxQty();
      if (this.useKitIndexAsQuantity) {
        if (this.usePresetSlotQuantity) return this._getRequiredQty();
        return this._getMaxKitQuantity() || this._getRequiredQty();
      }
      if (this.optionalExtraSlot) return this._optionalMaxExtraQty;
      return this._getRequiredQty();
    }

    _getMaxSelectableQtyForKitValue(kitValue, presetIndex) {
      if (!this.flexibleSelection) {
        return this._getPresetQuantityForKitValue(kitValue) || this._getRequiredQty();
      }

      const index =
        parseInt(presetIndex, 10) || this._getPresetIndexForKitValue(kitValue) || 1;
      const config = this._getFlexiblePresetConfig(index);
      const targetQty = this._getPresetQuantityForKitValue(kitValue);

      if (config?.autoUpgrade && targetQty) {
        return this._getAutoUpgradeMaxSelectableQty(config);
      }
      if (config?.unlimitedExtras || config?.allowExtras) return this._flexibleMaxExtraQty;
      return targetQty || this._getBaseRequiredQty();
    }

    _getFlexiblePresetConfig(presetIndex) {
      if (!this.flexibleSelection || !this.flexibleConfig?.presets) return null;
      return this.flexibleConfig.presets[String(presetIndex)] || null;
    }

    _getAutoUpgradeTargetPresetQty(config) {
      const upgradeTo = parseInt(config?.upgradeToPreset, 10) || 2;
      const row = this.querySelector(`[data-bundle-kit-row][data-preset-index="${upgradeTo}"]`);
      return parseInt(row?.getAttribute('data-preset-quantity'), 10) || 0;
    }

    _getAutoUpgradeMaxSelectableQty(config) {
      const baseQty = this._getBaseRequiredQty();
      const targetQty = this._getAutoUpgradeTargetPresetQty(config);
      if (targetQty > baseQty) return targetQty;
      return baseQty + 1;
    }

    _getBaseRequiredQty() {
      const presetQty = parseInt(this.activePreset?.quantity, 10);
      if (presetQty) return presetQty;
      const rowQty = parseInt(this._getSelectedKitRow()?.getAttribute('data-preset-quantity'), 10);
      if (rowQty) return rowQty;
      return 1;
    }

    _getEffectiveSelectionCount() {
      if (this.drawer?.open) {
        return this._getDrawerTotalQty();
      }
      return this.selections.length;
    }

    _getPricingQuantity() {
      if (this.optionalExtraSlot && this.useKitIndexAsQuantity) {
        const count = this._getEffectiveSelectionCount();
        if (count <= 0) return this._getBaseRequiredQty();
        return count;
      }
      if (!this.flexibleSelection) {
        return this._getRequiredQty();
      }
      const effective = this._getEffectiveSelectionCount();
      const base = this._getBaseRequiredQty();
      return Math.max(effective, base);
    }

    _getOptionalTierQuantity(count) {
      const row = this._getKitRowForSelectionCount(count);
      return parseInt(row?.getAttribute('data-preset-quantity'), 10) || count;
    }

    _getOptionalPricingDiscount() {
      const count = this._getEffectiveSelectionCount();
      const tierQty = count > 0 ? this._getOptionalTierQuantity(count) : this._getBaseRequiredQty();
      const preset = this._getPresetForQuantity(tierQty);
      return preset?.discountPercent || this.activePreset?.discountPercent || 0;
    }

    _getOptionalPricingKitRow(count) {
      if (count <= 0) return this._getSelectedKitRow();
      return this._getKitRowForSelectionCount(count) || this._getSelectedKitRow();
    }

    _getPresetForQuantity(qty) {
      const parsedQty = parseInt(qty, 10);
      if (!parsedQty) return null;
      return (
        this.presets.find((preset) => preset.quantity === parsedQty) ||
        this.presets.find((preset) => preset.presetIndex === parsedQty) ||
        null
      );
    }

    _calcOptionalTierPricing(qty) {
      const row = this._getKitRowForSelectionCount(qty);
      if (!row) return { finalTotal: 0, compareTotal: 0, savings: 0 };
      const preset = this._getPresetForRow(row);
      return this._calcPresetPricing(
        qty,
        preset?.discountPercent || 0,
        {},
        this._getKitVariantCents(row),
        this._getKitVariantCompareCents(row),
        preset?.presetIndex
      );
    }

    _getOptionalUpgradeProgress() {
      if (!this.optionalExtraSlot || !this.useKitIndexAsQuantity) return null;

      const count = this._getEffectiveSelectionCount();
      const currentRequired = this._getBaseRequiredQty();
      if (count < currentRequired) return null;

      const rows = [...this.querySelectorAll('[data-bundle-kit-row]')];
      const quantities = rows
        .map((row) => parseInt(row.getAttribute('data-preset-quantity'), 10) || 0)
        .filter((qty) => qty > 0)
        .sort((a, b) => a - b);
      const nextQty = quantities.find((qty) => qty > count);
      if (!nextQty) {
        if (this._isAtMaxKitTier() && count >= currentRequired) {
          return { type: 'bestprice' };
        }
        return null;
      }

      const remaining = nextQty - count;
      const currentPricing = this._calcOptionalTierPricing(currentRequired);
      const nextPricing = this._calcOptionalTierPricing(nextQty);
      const additionalSavings = Math.max(0, nextPricing.savings - currentPricing.savings);
      const savingsText = formatMoney(additionalSavings);

      return {
        remaining,
        html: `Wähle <strong>${remaining}</strong> mehr und <strong>spare weitere ${savingsText}</strong>`
      };
    }

    _shouldTriggerCurrentGoalCelebration(total, previousTotal, required) {
      if (required <= 0 || total < required || previousTotal >= required) return false;

      if (this.flexibleSelection) {
        return false;
      }

      if (this.optionalExtraSlot && this.useKitIndexAsQuantity) return true;

      return !this.optionalExtraSlot;
    }

    _shouldClearGoalCelebration(total, required) {
      if (this.flexibleSelection) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if (presetIndex === 2) return total < required;
        return true;
      }
      if (this.optionalExtraSlot && this.useKitIndexAsQuantity) return total < required;
      return !this.optionalExtraSlot && total < required;
    }

    _getGoalCelebrationMessage(total, required) {
      if (this.flexibleSelection && parseInt(this.activePreset?.presetIndex, 10) === 2) {
        return (
          this.flexibleConfig?.profiBestpriceGoalText || 'Wow! Du hast den Bestpreis erreicht!'
        );
      }
      if (
        this.optionalExtraSlot &&
        this.useKitIndexAsQuantity &&
        this._isAtMaxKitTier() &&
        total >= required
      ) {
        return (
          this.flexibleConfig?.bestpriceGoalText ||
          this.getAttribute('data-bestprice-goal-text') ||
          'Wow! Du hast den Bestpreis erreicht!'
        );
      }
      return null;
    }

    _getBestpriceProgressText() {
      return (
        this.flexibleConfig?.bestpriceProgressText ||
        '🛒 Shoppe unbegrenzt weiter zum Bestpreis'
      );
    }

    _isBestpriceGoalMessage(message) {
      if (!message) return false;
      const knownTexts = [
        this.flexibleConfig?.profiBestpriceGoalText,
        this.flexibleConfig?.bestpriceGoalText,
        this.getAttribute('data-bestprice-goal-text'),
        'Wow! Du hast den Bestpreis erreicht!'
      ].filter(Boolean);
      return knownTexts.includes(message) || /bestpreis/i.test(message);
    }

    _getBestpriceGoalToastDuration() {
      const configured = parseInt(this.flexibleConfig?.bestpriceGoalDuration, 10);
      return Number.isFinite(configured) && configured > 0 ? configured : 4000;
    }

    _updateOptionalProgressBanner() {
      this._ensureDrawerUpgradeProgressRefs();
      this._updateDrawerUpgradeProgress(this._getOptionalUpgradeProgress());
    }

    _getEffectivePresetIndexForPricing() {
      const presetIndex = parseInt(this.activePreset?.presetIndex, 10) || 1;
      if (!this.flexibleSelection) return presetIndex;

      const config = this._getFlexiblePresetConfig(presetIndex);
      if (config?.autoUpgrade) {
        const effectiveQty = this._getEffectiveSelectionCount();
        const baseQty = this._getBaseRequiredQty();
        const upgradeTo = parseInt(config.upgradeToPreset, 10) || 2;
        if (effectiveQty > baseQty) return upgradeTo;
      }

      return presetIndex;
    }

    _getFlexibleDiscountPercent(presetIndex, effectiveQty) {
      const preset = this.presets.find((p) => p.presetIndex === presetIndex);
      const config = this._getFlexiblePresetConfig(presetIndex);

      if (config?.volumeTiers?.length) {
        let discount = preset?.discountPercent || 0;
        [...config.volumeTiers]
          .sort((a, b) => a.quantity - b.quantity)
          .forEach((tier) => {
            if (effectiveQty >= tier.quantity) {
              discount = this._parseDiscountPercent(tier.discountPercent);
            }
          });
        return discount;
      }

      if (config?.autoUpgrade) {
        const baseQty = this._getBaseRequiredQty();
        const upgradeTo = parseInt(config.upgradeToPreset, 10) || 2;
        if (effectiveQty > baseQty) {
          const targetPreset = this.presets.find((p) => p.presetIndex === upgradeTo);
          return targetPreset?.discountPercent || 0;
        }
        return 0;
      }

      return preset?.discountPercent || 0;
    }

    _getActiveDiscountPercent() {
      if (this.optionalExtraSlot && this.useKitIndexAsQuantity) {
        return this._getOptionalPricingDiscount();
      }
      if (!this.flexibleSelection) {
        return this.activePreset?.discountPercent || 0;
      }
      const presetIndex = parseInt(this.activePreset?.presetIndex, 10) || 1;
      const pricingPresetIndex = this._getEffectivePresetIndexForPricing();
      const effectiveQty =
        presetIndex === 3 ? this._getEffectiveSelectionCount() : this._getPricingQuantity();
      if (presetIndex === 3) {
        return this._getFlexibleDiscountPercent(presetIndex, effectiveQty);
      }
      return this._getFlexibleDiscountPercent(pricingPresetIndex, effectiveQty);
    }

    _getBundleGroupWithGoodie() {
      const fromAttr = (this.getAttribute('data-bundle-group-with-goodie') || '').trim();
      if (fromAttr) return fromAttr;
      const fromProp = (this.bundleGroupWithGoodie || '').trim();
      if (fromProp) return fromProp;
      return '';
    }

    _hasSelectedOptInGoodie() {
      const item = this._getGiftItem(1);
      if (!item || !this._isGiftOptIn(item)) return false;
      if (!this._giftAppliesToCurrentPreset(item)) return false;
      return Boolean(parseInt(this.giftSelections[item.slot], 10));
    }

    _shouldUseGoodieBundleGroup() {
      return Boolean(this._getBundleGroupWithGoodie()) && this._hasSelectedOptInGoodie();
    }

    _getEffectiveBundleGroup() {
      const baseGroup = (this.bundleGroup || '').trim();
      if (baseGroup.toLowerCase() === 'superlimo' && !this._hasSelectedOptInGoodie()) {
        return 'superlimo mit goodie';
      }

      if (this._shouldUseGoodieBundleGroup()) {
        return this._getBundleGroupWithGoodie();
      }

      if (!this.flexibleSelection) return this.bundleGroup;

      const pricingPresetIndex = this._getEffectivePresetIndexForPricing();
      const config = this._getFlexiblePresetConfig(pricingPresetIndex);
      if (config?.bundleGroup) return config.bundleGroup;

      const presetGroup = this.activePreset?.bundleGroup;
      if (presetGroup) return presetGroup;

      const rowGroup = this._getSelectedKitRow()?.getAttribute('data-bundle-group');
      if (rowGroup) return rowGroup;

      return this.bundleGroup;
    }

    _getDiscountScaleQuantity() {
      return this._getPricingQuantity() || this._getRequiredQty() || 0;
    }

    _getFlexibleMaxQty() {
      const presetIndex = parseInt(this.activePreset?.presetIndex, 10) || 1;
      const config = this._getFlexiblePresetConfig(presetIndex);
      if (config?.autoUpgrade) {
        return this._getAutoUpgradeMaxSelectableQty(config);
      }
      if (config?.unlimitedExtras || config?.allowExtras) {
        return this._flexibleMaxExtraQty;
      }
      return this._getBaseRequiredQty();
    }

    _getMaxKitQuantity() {
      const rows = [...this.querySelectorAll('[data-bundle-kit-row]')];
      if (!rows.length) return 0;
      return Math.max(
        ...rows.map((row) => parseInt(row.getAttribute('data-preset-quantity'), 10) || 0),
        0
      );
    }

    _isAtMaxKitTier() {
      const maxQty = this._getMaxKitQuantity();
      if (!maxQty) return false;
      return this._getBaseRequiredQty() >= maxQty;
    }

    _checkFlexibleUpgrade() {
      if (!this.flexibleSelection || this._flexibleUpgradeInProgress) return;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      const config = this._getFlexiblePresetConfig(presetIndex);
      if (!config?.autoUpgrade) return;

      const effectiveQty = this._getEffectiveSelectionCount();
      const baseQty = this._getBaseRequiredQty();
      const upgradeTo = parseInt(config.upgradeToPreset, 10) || 2;

      if (effectiveQty <= baseQty || presetIndex === upgradeTo) return;

      const row = this.querySelector(`[data-bundle-kit-row][data-preset-index="${upgradeTo}"]`);
      if (!row) return;

      this._flexibleAutoUpgraded = true;
      this._autoSelectKit(row, { preserveSelections: true });
    }

    _getFlexibleStarterRow() {
      return this.querySelector('[data-bundle-kit-row][data-preset-index="1"]');
    }

    _getFlexibleStarterQty() {
      const starterRow = this._getFlexibleStarterRow();
      return parseInt(starterRow?.getAttribute('data-preset-quantity'), 10) || 4;
    }

    _checkFlexibleDowngrade() {
      if (!this.flexibleSelection || this._flexibleUpgradeInProgress) return;
      if (!this._flexibleAutoUpgraded) return;

      const starterConfig = this._getFlexiblePresetConfig(1);
      if (!starterConfig?.autoUpgrade) return;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      const upgradeTo = parseInt(starterConfig.upgradeToPreset, 10) || 2;
      if (presetIndex !== upgradeTo) return;

      const starterRow = this._getFlexibleStarterRow();
      if (!starterRow) return;

      const effectiveQty = this._getEffectiveSelectionCount();
      if (effectiveQty > this._getFlexibleStarterQty()) return;

      this._autoSelectKit(starterRow, { preserveSelections: true });
    }

    _getKitRowForSelectionCount(count) {
      const rows = [...this.querySelectorAll('[data-bundle-kit-row]')];
      if (!rows.length || count <= 0) return null;

      const presetQuantities = rows
        .map((row) => parseInt(row.getAttribute('data-preset-quantity'), 10) || 0)
        .filter((qty) => qty > 0);
      const maxQty = Math.max(...presetQuantities, 0);
      const targetQty = Math.min(count, maxQty);

      return (
        rows.find((row) => parseInt(row.getAttribute('data-preset-quantity'), 10) === targetQty) ||
        null
      );
    }

    _checkOptionalKitSync() {
      if (!this.useKitIndexAsQuantity || this._flexibleUpgradeInProgress) {
        return;
      }

      const count = this._getEffectiveSelectionCount();
      if (count <= 0) return;

      const targetRow = this._getKitRowForSelectionCount(count);
      if (!targetRow) return;

      const currentQty = this._getBaseRequiredQty();
      const targetQty = parseInt(targetRow.getAttribute('data-preset-quantity'), 10);
      if (targetQty <= currentQty) return;

      this._autoSelectKit(targetRow, { preserveSelections: true });
    }

    _syncOptionalAfterSelectionChange() {
      if (!this.optionalExtraSlot) return;
      this._checkOptionalKitSync();
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      this._updateDrawerPricing();
      this._syncDrawerQtyDisplay();
      this._updateOptionalProgressBanner();
      this._updateUi();
    }

    _autoSelectKit(row, options = {}) {
      const { preserveSelections = false } = options;
      if (!row || this._flexibleUpgradeInProgress) return;

      this._flexibleUpgradeInProgress = true;
      try {
        const kitValue = row.getAttribute('data-kit-value');
        const optionValueId = row.getAttribute('data-option-value-id');
        const presetIndex = row.getAttribute('data-preset-index');
        const variantId = row.getAttribute('data-variant-id');
        const preservedSelections = preserveSelections ? [...this.selections] : null;
        const preservedDrawerQty =
          preserveSelections && this.drawer?.open ? { ...this.drawerQuantities } : null;

        if (variantId) {
          this._setFormVariantId(variantId);
        }
        this._setKitOptionInForm(optionValueId);
        this._syncGalleryForKitRow(row);

        this.querySelectorAll('[data-bundle-kit-row]').forEach((r) => {
          r.classList.toggle('is-selected', r === row);
          r.toggleAttribute('aria-current', r === row);
        });

        const previousKit = this.activePreset?.kitValue;
        if (previousKit && previousKit !== kitValue) {
          this._saveSelectionsForPreset(previousKit);
        }

        this._applyPresetForKit(kitValue, presetIndex);

        if (preserveSelections) {
          if (preservedSelections) this.selections = this._sanitizeSelections(preservedSelections);
          if (preservedDrawerQty) {
            const sanitizedDrawerQty = {};
            Object.entries(preservedDrawerQty).forEach(([variantId, count]) => {
              const product = this.products.find((entry) => String(entry.variant_id) === variantId);
              if (this._isProductAvailable(product) && count) {
                sanitizedDrawerQty[variantId] = count;
              }
            });
            this.drawerQuantities = sanitizedDrawerQty;
          }
          if (kitValue) this._saveSelectionsForPreset(kitValue);
        } else {
          const incoming = preservedSelections?.length
            ? preservedSelections
            : this._getIncomingSelections(previousKit);
          this._loadSelectionsForPreset(kitValue, previousKit, incoming);
        }

        this._renderSlots();
        if (this.flexibleSelection) {
          this._updateFlexibleProgressBanner();
        }
        this._updateKitRowPricing();
        this._updateKitRowSwitchability();
        this._updateUi();
        this._updateDrawerPricing();
        this._syncDrawerQtyDisplay();
      } finally {
        this._flexibleUpgradeInProgress = false;
      }
    }

    _getFlexibleUpgradeProgress() {
      if (!this.flexibleSelection) return null;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      const config = this._getFlexiblePresetConfig(presetIndex);
      if (!config?.autoUpgrade) return null;
      if (presetIndex === 1) return null;

      const effectiveQty = this._getEffectiveSelectionCount();
      const baseQty = this._getBaseRequiredQty();
      const upgradeAt = parseInt(config.upgradeAtQuantity, 10) || 8;
      const upgradeTo = parseInt(config.upgradeToPreset, 10) || 2;

      if (effectiveQty >= upgradeAt || effectiveQty < baseQty) return null;
      if (!config.upgradeRewardLabel) return null;
      if (presetIndex === upgradeTo) return null;

      const remaining = upgradeAt - effectiveQty;
      const rewardLabel = String(config.upgradeRewardLabel)
        .replace(/^gratis\s+/i, '')
        .trim();

      return {
        remaining,
        rewardLabel: config.upgradeRewardLabel,
        progressText: `Noch ${remaining} für`,
        badgeText: rewardLabel ? `GRATIS ${rewardLabel}` : 'GRATIS'
      };
    }

    _getFlexibleVolumeProgress() {
      if (!this.flexibleSelection) return null;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      if (presetIndex !== 3) return null;

      const config = this._getFlexiblePresetConfig(3);
      if (!config?.volumeTiers?.length) return null;

      const count = this._getEffectiveSelectionCount();
      const tiers = [...config.volumeTiers].sort((a, b) => a.quantity - b.quantity);

      for (let i = 0; i < tiers.length; i += 1) {
        const tier = tiers[i];
        const tierQty = parseInt(tier.quantity, 10) || 0;
        if (!tierQty || count >= tierQty) continue;

        const remaining = tierQty - count;
        const discount = this._parseDiscountPercent(tier.discountPercent);
        return {
          remaining,
          rewardLabel: `${discount}% Rabatt`,
          progressText: `Noch ${remaining} für`,
          badgeText: `${discount}% RABATT`
        };
      }

      return null;
    }

    _getFlexibleStarterProfiSavingsProgress() {
      if (!this.flexibleSelection) return null;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      if (presetIndex !== 1) return null;

      const starterQty = this._getBaseRequiredQty();
      const count = this._getEffectiveSelectionCount();
      if (!starterQty || count < starterQty) return null;

      const starterRow = this.querySelector('[data-bundle-kit-row][data-preset-index="1"]');
      const profiRow = this.querySelector('[data-bundle-kit-row][data-preset-index="2"]');
      if (!starterRow || !profiRow) return null;

      const starterPreset = this._getPresetForRow(starterRow);
      const profiPreset = this._getPresetForRow(profiRow);
      if (!starterPreset || !profiPreset) return null;

      const profiQty = parseInt(profiRow.getAttribute('data-preset-quantity'), 10) || 8;
      const quantities = this._selectionsToQuantities(this.selections || []);

      const starterPricing = this._calcPresetPricing(
        starterQty,
        starterPreset.discountPercent || 0,
        quantities,
        this._getKitVariantCents(starterRow),
        this._getKitVariantCompareCents(starterRow),
        starterPreset.presetIndex
      );

      const profiPricing = this._calcPresetPricing(
        profiQty,
        profiPreset.discountPercent || 0,
        quantities,
        this._getKitVariantCents(profiRow),
        this._getKitVariantCompareCents(profiRow),
        profiPreset.presetIndex
      );

      const additionalSavings = Math.max(0, profiPricing.savings - starterPricing.savings);
      if (additionalSavings <= 0) return null;

      return {
        type: 'starter-profi-upsell',
        action: 'switch-profi',
        html: this._buildStarterProfiSavingsHtml(additionalSavings)
      };
    }

    _buildStarterProfiSavingsHtml(amountCents) {
      const amount = formatMoney(amountCents);
      const kitLabel = this.flexibleConfig?.starterProfiSavingsBadge || 'Profi-Kit';
      const template =
        this.flexibleConfig?.starterProfiSavingsText || 'Spare weitere __AMOUNT__ im';
      const amountBadge = `<span class="badge badge--primary bk-media-bundle-builder__flexible-progress-inline-badge">${amount}</span>`;
      const text = template.replace(/__AMOUNT__|\{amount\}/g, amountBadge);
      return `${text} ${kitLabel}`;
    }

    _getFlexibleProfiBestpriceProgress() {
      if (!this.flexibleSelection) return null;

      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      if (presetIndex !== 2) return null;

      const config = this._getFlexiblePresetConfig(presetIndex);
      if (!config?.allowExtras && !config?.unlimitedExtras) return null;

      const count = this._getEffectiveSelectionCount();
      const baseQty = this._getBaseRequiredQty();
      if (!baseQty || count < baseQty) return null;

      return { type: 'bestprice' };
    }

    _getFlexibleProgress() {
      return this._getFlexibleUpgradeProgress() || this._getFlexibleVolumeProgress();
    }

    _setFlexibleProgressInteractiveState(element, progress) {
      if (!element) return;
      const isClickable = progress?.action === 'switch-profi';
      element.classList.toggle('is-clickable', isClickable);
      if (isClickable) {
        element.setAttribute('role', 'button');
        element.tabIndex = 0;
      } else {
        element.removeAttribute('role');
        element.removeAttribute('tabindex');
      }
    }

    _switchToProfiKitFromStarterUpsell(options = {}) {
      const row = this.querySelector('[data-bundle-kit-row][data-preset-index="2"]');
      if (!row) return;
      const kitValue = row.getAttribute('data-kit-value');
      if (!this._canSwitchToKit(kitValue)) {
        this._highlightMissing(row);
        return;
      }
      const keepDrawerOpen = options.fromDrawer ?? Boolean(this.drawer?.open);
      this._selectKit(row, { keepDrawerOpen });
    }

    _handleFlexibleProgressAction(progress, options = {}) {
      if (progress?.action === 'switch-profi') {
        this._switchToProfiKitFromStarterUpsell(options);
      }
    }

    _updateFlexibleProgressBanner() {
      const banner = this.querySelector('[data-flexible-progress-banner]');
      const progressTextEl = this.querySelector('[data-flexible-progress-text]');
      const progressBadgeEl = this.querySelector('[data-flexible-progress-badge]');
      const progress = this._getFlexibleProgress();
      const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
      const selectionCount = this._getEffectiveSelectionCount();
      const showBanner =
        Boolean(progress) && !(presetIndex === 3 && selectionCount < 1);

      if (banner) {
        if (!showBanner) {
          banner.hidden = true;
          this._setFlexibleProgressInteractiveState(banner, null);
          banner.classList.remove('is-inline-progress');
        } else {
          banner.hidden = false;

          if (progress.type === 'bestprice') {
            if (progressTextEl) {
              progressTextEl.textContent = this._getBestpriceProgressText();
            }
            if (progressBadgeEl) {
              progressBadgeEl.hidden = true;
              progressBadgeEl.textContent = '';
            }
          } else if (progress.html) {
            if (progressTextEl) {
              progressTextEl.innerHTML = progress.html;
            }
            if (progressBadgeEl) {
              progressBadgeEl.hidden = true;
              progressBadgeEl.textContent = '';
            }
          } else {
            if (progressTextEl) {
              progressTextEl.textContent = progress.progressText || '';
            }
            if (progressBadgeEl) {
              if (progress.badgeText) {
                progressBadgeEl.textContent = progress.badgeText;
                progressBadgeEl.hidden = false;
              } else {
                progressBadgeEl.hidden = true;
                progressBadgeEl.textContent = '';
              }
            }
          }

          this._setFlexibleProgressInteractiveState(banner, progress);
          banner.classList.toggle('is-inline-progress', progress.type === 'starter-profi-upsell');
        }
      }

      this._updateDrawerUpgradeProgress(progress);
    }

    _updateDrawerUpgradeProgress(progress) {
      const wrap = this.drawerUpgradeProgress;
      if (!wrap) return;

      if (!progress) {
        wrap.hidden = true;
        wrap.classList.remove('is-inline-progress');
        this._setFlexibleProgressInteractiveState(wrap, null);
        return;
      }

      if (progress.type === 'starter-profi-upsell') {
        wrap.hidden = true;
        wrap.classList.remove('is-inline-progress');
        this._setFlexibleProgressInteractiveState(wrap, null);
        return;
      }

      if (progress.type === 'bestprice') {
        wrap.hidden = false;
        if (this.drawerUpgradeProgressText) {
          this.drawerUpgradeProgressText.textContent = this._getBestpriceProgressText();
        }
        if (this.drawerUpgradeProgressBadge) {
          this.drawerUpgradeProgressBadge.hidden = true;
          this.drawerUpgradeProgressBadge.textContent = '';
        }
        this._setFlexibleProgressInteractiveState(wrap, null);
        return;
      }

      if (this.drawerUpgradeProgressText) {
        if (progress.html) {
          this.drawerUpgradeProgressText.innerHTML = progress.html;
        } else {
          this.drawerUpgradeProgressText.textContent = progress.progressText || '';
        }
      }
      if (this.drawerUpgradeProgressBadge) {
        if (progress.html || !progress.badgeText) {
          this.drawerUpgradeProgressBadge.hidden = true;
          this.drawerUpgradeProgressBadge.textContent = '';
        } else {
          this.drawerUpgradeProgressBadge.hidden = false;
          this.drawerUpgradeProgressBadge.textContent = progress.badgeText;
        }
      }
      wrap.hidden = false;
      wrap.classList.toggle('is-inline-progress', progress.type === 'starter-profi-upsell');
      this._setFlexibleProgressInteractiveState(wrap, progress);
    }

    _syncFlexibleAfterSelectionChange() {
      if (!this.flexibleSelection) return;
      this._checkFlexibleUpgrade();
      this._checkFlexibleDowngrade();
      this._updateFlexibleProgressBanner();
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      this._updateDrawerPricing();
      this._syncDrawerQtyDisplay();
      this._updateUi();
    }

    _applyBundleDiscount(bundleBaseCents, discountPercent) {
      const base = bundleBaseCents || 0;
      if (!base) return 0;
      const discountBps = Math.round(this._parseDiscountPercent(discountPercent) * 100);
      return Math.floor((base * (10000 - discountBps)) / 10000);
    }

    _applyCardsOrderDiscount(unitCents, quantity, discountPercent) {
      const unit = unitCents || 0;
      const qty = quantity || 0;
      if (!unit || !qty) return 0;
      const discount = this._parseDiscountPercent(discountPercent);
      const subtotal = unit * qty;

      if (this.cardsDiscountMode === 'order_total') {
        return this._applyBundleDiscount(subtotal, discount);
      }

      if (qty === 3) {
        const discountBps = Math.round(discount * 100);
        return Math.round((subtotal * (10000 - discountBps)) / 10000);
      }

      const discountBps = Math.round(discount * 100);
      const unitDiscount = Math.floor((unit * discountBps) / 10000);
      return (unit - unitDiscount) * qty;
    }

    _getCardsOnlyGiftCompare(quantity) {
      if (this.giftMode !== 'single_always' || quantity !== 1) return 0;
      return this._getGiftCompareForQuantity(quantity);
    }

    _setFormVariantId(variantId) {
      const form = this.form;
      if (!form || !variantId) return;
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      idInput.disabled = false;
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _getVariantById(variantId) {
      if (!variantId) return null;
      return this.variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    _buildGalleryVariantPayload(variant) {
      if (!variant) return null;
      return {
        id: variant.id,
        featured_media:
          variant.featured_media_id != null
            ? {
                id: variant.featured_media_id,
                position: variant.featured_media_position
              }
            : null
      };
    }

    _syncGalleryForKitRow(row) {
      if (!row || this.lockGallery) return;
      const variant = this._getVariantById(row.getAttribute('data-variant-id'));
      if (!variant) return;
      if (this.variantPicker) {
        this._applyGalleryFilterDom(variant);
        const nextVariant = this._buildGalleryVariantPayload(variant);
        this._dispatchGalleryVariantChange(this.form, nextVariant, this._lastGalleryVariant || null);
        this._lastGalleryVariant = nextVariant;
        return;
      }
      this._syncGalleryWithoutPicker(variant);
    }

    async _syncGalleryWithoutPicker(variant) {
      const form = this.form;
      if (!form || !variant?.id) return;

      const sectionId = this.getAttribute('data-section-id');
      const handle = this.getAttribute('data-product-handle');
      const previousVariant = this._lastGalleryVariant || null;
      const nextVariant = this._buildGalleryVariantPayload(variant);

      if (!sectionId || !handle) {
        this._applyGalleryFilterDom(variant);
        this._dispatchGalleryVariantChange(form, nextVariant, previousVariant);
        this._lastGalleryVariant = nextVariant;
        return;
      }

      const requestId = (this._gallerySyncRequestId = (this._gallerySyncRequestId || 0) + 1);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}products/${handle}?variant=${variant.id}&section_id=${encodeURIComponent(sectionId)}`;
        const html = await (await fetch(url)).text();
        if (requestId !== this._gallerySyncRequestId) return;

        const fragment = document.createRange().createContextualFragment(html);
        form.dispatchEvent(
          new CustomEvent('product:rerender', {
            detail: { htmlFragment: fragment, productChange: false }
          })
        );
        this._dispatchGalleryVariantChange(form, nextVariant, previousVariant);
        this._lastGalleryVariant = nextVariant;

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', String(variant.id));
        window.history.replaceState({ path: nextUrl.toString() }, '', nextUrl.toString());
      } catch (_error) {
        if (requestId !== this._gallerySyncRequestId) return;
        this._applyGalleryFilterDom(variant);
        this._dispatchGalleryVariantChange(form, nextVariant, previousVariant);
        this._lastGalleryVariant = nextVariant;
      }
    }

    _dispatchGalleryVariantChange(form, variant, previousVariant) {
      if (!form || !variant) return;
      this._suppressVariantChangeHandling = true;
      try {
        const script = this.querySelector('script[data-variant]');
        if (script) {
          script.textContent = JSON.stringify(variant);
        }
        form.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              formId: form.id,
              variant,
              previousVariant
            }
          })
        );
      } finally {
        this._suppressVariantChangeHandling = false;
      }
    }

    _applyGalleryFilterDom(variant) {
      const gallery = document.querySelector(`product-gallery[form="${this.formId}"]`);
      if (!gallery || !variant) return;

      const kitValue = String(getVariantOptions(variant)[this.kitOptionIndex] || '')
        .trim()
        .toLowerCase();
      if (!kitValue) return;

      const mediaItems = Array.from(gallery.querySelectorAll('.product-gallery__media'));
      const filteredIndexes = [];

      mediaItems.forEach((mediaEl, index) => {
        const alt =
          mediaEl.querySelector('img')?.getAttribute('alt') ||
          mediaEl.querySelector('img')?.alt ||
          '';
        if (!alt.includes('#')) return;

        const groupValue = alt.split('#').pop().split('_').pop().trim().toLowerCase();
        if (groupValue && groupValue !== kitValue) {
          filteredIndexes.push(index);
        }
      });

      gallery.setAttribute(
        'filtered-indexes',
        JSON.stringify(filteredIndexes.map((index) => index + 1))
      );

      gallery.querySelectorAll('media-carousel').forEach((carousel) => {
        carousel.dispatchEvent(
          new CustomEvent('carousel:filter', { detail: { filteredIndexes } })
        );
      });
      gallery.querySelectorAll('page-dots').forEach((pageDots) => {
        pageDots.dispatchEvent(
          new CustomEvent('control:filter', { detail: { filteredIndexes } })
        );
      });
    }

    _getSelectedKitVariantId() {
      const selectedRow = this.querySelector('[data-bundle-kit-row].is-selected');
      const rowVariantId = selectedRow?.getAttribute('data-variant-id');
      if (rowVariantId) return parseInt(rowVariantId, 10);
      return this._getCurrentVariant()?.id || null;
    }

    _bindEvents() {
      this.querySelectorAll('[data-bundle-kit-row]').forEach((row) => {
        row.addEventListener('click', (e) => {
          const giftTrigger = e.target.closest('[data-bundle-kit-gift-trigger]');
          if (giftTrigger) {
            e.preventDefault();
            e.stopPropagation();
            if (!row.classList.contains('is-selected')) {
              const kitValue = row.getAttribute('data-kit-value');
              if (!this._canSwitchToKit(kitValue)) {
                this._highlightMissing(row);
                return;
              }
              this._selectKit(row);
            }
            const slot = parseInt(giftTrigger.getAttribute('data-gift-slot'), 10);
            const item = slot ? this._getGiftItem(slot) : null;
            if (item && (item.variants?.length || 0) > 1) {
              this._openColorsDrawer(slot);
            }
            return;
          }

          const kitValue = row.getAttribute('data-kit-value');
          if (!this._canSwitchToKit(kitValue)) {
            this._highlightMissing(row);
            return;
          }
          this._selectKit(row);
        });
      });

      this.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-slot-remove]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const index = parseInt(removeBtn.getAttribute('data-slot-index'), 10);
          if (!Number.isNaN(index)) {
            this._removeSelectionAtIndex(index);
          }
          return;
        }
        const giftRemoveBtn = e.target.closest('[data-gift-slot-remove]');
        if (giftRemoveBtn) {
          e.preventDefault();
          e.stopPropagation();
          const slot = parseInt(giftRemoveBtn.getAttribute('data-gift-slot'), 10);
          if (!Number.isNaN(slot)) {
            this._removeGiftSelection(slot);
          }
          return;
        }
        if (e.target.closest('[data-bundle-open-drawer]')) {
          this._openDrawer();
          return;
        }
        const giftOpen = e.target.closest('[data-bundle-gift-open]');
        if (giftOpen) {
          e.preventDefault();
          e.stopPropagation();
          if (e.target.closest('.bk-media-bundle-builder__gift-qty')) return;
          const slot = parseInt(giftOpen.getAttribute('data-gift-slot'), 10);
          if (slot) this._openGiftModalForSlot(slot);
          return;
        }
        const colorsOpen = e.target.closest('[data-bundle-open-colors-drawer]');
        if (colorsOpen) {
          const slot = parseInt(colorsOpen.getAttribute('data-gift-slot'), 10);
          if (slot) this._openColorsDrawer(slot);
          return;
        }

        const progressTrigger = e.target.closest(
          '[data-flexible-progress-banner]:not([hidden])'
        );
        if (progressTrigger?.classList.contains('is-clickable')) {
          e.preventDefault();
          this._handleFlexibleProgressAction(this._getFlexibleProgress(), { fromDrawer: false });
          return;
        }

        const featureScroll = e.target.closest('[data-bundle-feature-scroll]');
        if (featureScroll) {
          const selector = featureScroll.getAttribute('data-bundle-feature-scroll');
          if (!selector) return;
          const target = document.querySelector(selector);
          if (!target) return;
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const progressTrigger = e.target.closest(
          '[data-flexible-progress-banner].is-clickable'
        );
        if (progressTrigger) {
          e.preventDefault();
          this._handleFlexibleProgressAction(this._getFlexibleProgress(), { fromDrawer: false });
          return;
        }
        const kitGiftTrigger = e.target.closest('[data-bundle-kit-gift-trigger][role="button"]');
        if (kitGiftTrigger) {
          e.preventDefault();
          const kitRow = kitGiftTrigger.closest('[data-bundle-kit-row]');
          if (kitRow && !kitRow.classList.contains('is-selected')) {
            const kitValue = kitRow.getAttribute('data-kit-value');
            if (!this._canSwitchToKit(kitValue)) {
              this._highlightMissing(kitRow);
              return;
            }
            this._selectKit(kitRow);
          }
          const slot = parseInt(kitGiftTrigger.getAttribute('data-gift-slot'), 10);
          const item = slot ? this._getGiftItem(slot) : null;
          if (item && (item.variants?.length || 0) > 1) {
            this._openColorsDrawer(slot);
          }
          return;
        }
        const colorsOpen = e.target.closest('[data-bundle-open-colors-drawer][role="button"]');
        if (colorsOpen && colorsOpen.tagName !== 'BUTTON') {
          e.preventDefault();
          const slot = parseInt(colorsOpen.getAttribute('data-gift-slot'), 10);
          if (slot) this._openColorsDrawer(slot);
          return;
        }
        const giftOpen = e.target.closest('[data-bundle-gift-open][role="button"]');
        if (!giftOpen || giftOpen.tagName === 'BUTTON') return;
        e.preventDefault();
        const slot = parseInt(giftOpen.getAttribute('data-gift-slot'), 10);
        if (slot) this._openGiftModalForSlot(slot);
      });

      this._bindDrawerEvents();

      this.atcButton?.addEventListener('click', () => this._addToCart());

      const form = this.form;
      if (form) {
        form.addEventListener('variant:change', (e) => this._onVariantChange(e));
      }
    }

    _bindDrawerEvents() {
      if (!this.drawer || this._drawerEventsBound) return;
      this._drawerEventsBound = true;

      this.drawerConfirm?.addEventListener('click', () => this._confirmDrawer());

      this.drawerList?.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-qty-minus]');
        const plus = e.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        const item = e.target.closest('[data-drawer-item]');
        const variantId = parseInt(item?.getAttribute('data-variant-id'), 10);
        if (!variantId) return;
        this._changeDrawerQty(variantId, plus ? 1 : -1);
      });

      this.drawerList?.addEventListener('keydown', (e) => {
        if (e.target.matches('[data-qty-value]')) e.preventDefault();
      });

      this.drawer.addEventListener('click', (e) => {
        const progressTrigger = e.target.closest(
          '[data-drawer-upgrade-progress]:not([hidden])'
        );
        if (!progressTrigger?.classList.contains('is-clickable')) return;
        e.preventDefault();
        this._handleFlexibleProgressAction(this._getFlexibleProgress(), { fromDrawer: true });
      });

      this.drawer.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const progressTrigger = e.target.closest(
          '[data-drawer-upgrade-progress].is-clickable'
        );
        if (!progressTrigger) return;
        e.preventDefault();
        this._handleFlexibleProgressAction(this._getFlexibleProgress(), { fromDrawer: true });
      });

      this.drawer.addEventListener('dialog:after-hide', () => {
        this._hideUniqueLimitNotes();
        if (this._suppressDrawerPersist) {
          this._suppressDrawerPersist = false;
          this.drawerQuantities = {};
          this._updateKitRowPricing();
          this._updateDrawerPricing();
          return;
        }
        this._persistDrawerSelections();
        this.drawerQuantities = {};
        this._updateKitRowPricing();
        this._updateDrawerPricing();
      });
    }

    _initColorsDrawer() {
      const drawerId = this.getAttribute('data-colors-drawer-id');
      if (!drawerId) return;

      this.colorsDrawer = document.getElementById(drawerId);
      this.colorsDrawerList =
        this.colorsDrawer?.querySelector('[data-colors-drawer-list]') ||
        this.querySelector('[data-colors-drawer-list]');
      this.colorsPickerStatus =
        this.colorsDrawer?.querySelector('[data-colors-picker-status]') ||
        this.querySelector('[data-colors-picker-status]');
      this.colorsPickerConfirm =
        this.colorsDrawer?.querySelector('[data-colors-picker-confirm]') ||
        this.querySelector('[data-colors-picker-confirm]');
      this.colorsPickerConfirmCount =
        this.colorsDrawer?.querySelector('[data-colors-picker-confirm-count]') ||
        this.querySelector('[data-colors-picker-confirm-count]');
      this.colorsDrawerCelebration =
        this.colorsDrawer?.querySelector('[data-colors-drawer-celebration]') ||
        this.querySelector('[data-colors-drawer-celebration]');
      this.colorsDrawerGoalToast =
        this.colorsDrawer?.querySelector('[data-colors-drawer-goal-toast]') ||
        this.querySelector('[data-colors-drawer-goal-toast]');
      if (!this.colorsDrawer || !this.colorsDrawerList) return;

      Object.defineProperty(this.colorsDrawer, 'openFrom', {
        get: () => 'right',
        configurable: true
      });
      if (!this.colorsDrawer.hasAttribute('tabindex')) {
        this.colorsDrawer.setAttribute('tabindex', '-1');
      }

      this._bindColorsDrawerListEvents();
      this.colorsPickerConfirm?.addEventListener('click', () => this._confirmColorsDrawer());
      this.colorsDrawer.addEventListener('dialog:after-hide', () => {
        if (!this._activeColorsSlot) return;
        this._persistColorsDrawerSelection(this._activeColorsSlot);
        this._activeColorsSlot = null;
        this._activePickerContext = null;
      });
      this.colorsDrawer.addEventListener('dialog:after-show', () => {
        this.colorsDrawer?.removeAttribute('inert');
        this._syncColorsDrawerState();
      });
    }

    _bindColorsDrawerListEvents() {
      if (!this.colorsDrawerList || this._colorsListEventsBound) return;
      this._colorsListEventsBound = true;

      this.colorsDrawerList.addEventListener('change', (e) => {
        if (!e.target.matches?.('input[type="radio"]')) return;
        this._syncColorsDrawerState();
        if (this._activeColorsSlot) {
          this._persistColorsDrawerSelection(this._activeColorsSlot);
        }
      });

      this.colorsDrawerList.addEventListener('click', (e) => {
        const row = e.target.closest('[data-colors-variant-row]');
        if (!row || !this.colorsDrawerList.contains(row)) return;
        if (row.classList.contains('is-unavailable')) return;

        const input = row.querySelector('input[type="radio"]');
        if (!input || input.disabled) return;

        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        requestAnimationFrame(() => {
          this._syncColorsDrawerState();
          if (this._activeColorsSlot) {
            this._persistColorsDrawerSelection(this._activeColorsSlot);
          }
        });
      });
    }

    _initGiftDrawer() {
      const blockId = this.getAttribute('data-block-id');
      if (!blockId) return;
      this.giftDrawer = document.getElementById(`bundle-gift-drawer-${blockId}`);
      this.giftDrawerContent =
        this.giftDrawer?.querySelector('[data-gift-drawer-content]') ||
        this.querySelector('[data-gift-drawer-content]');
      this.giftDrawerHeader =
        this.giftDrawer?.querySelector('[data-gift-drawer-header]') ||
        this.querySelector('[data-gift-drawer-header]');
      this.giftDrawerFooter =
        this.giftDrawer?.querySelector('[data-gift-drawer-footer]') ||
        this.querySelector('[data-gift-drawer-footer]');
      this.giftPickerStatus =
        this.giftDrawer?.querySelector('[data-gift-picker-status]') ||
        this.querySelector('[data-gift-picker-status]');
      this.giftPickerConfirm =
        this.giftDrawer?.querySelector('[data-gift-picker-confirm]') ||
        this.querySelector('[data-gift-picker-confirm]');
      if (!this.giftDrawer || !this.giftDrawerContent) return;

      Object.defineProperty(this.giftDrawer, 'openFrom', {
        get: () => this._getDrawerOpenFrom('right'),
        configurable: true
      });

      this.giftPickerConfirm?.addEventListener('click', () => {
        if (this._activeGiftPickerSlot) this._confirmGiftPicker(this._activeGiftPickerSlot);
      });
    }

    _initGiftModalDescription() {
      const desc = this.giftDrawerContent?.querySelector('[data-gift-description]');
      const toggle = this.giftDrawerContent?.querySelector('[data-gift-read-toggle]');
      if (!desc || !toggle) return;

      desc.classList.add('is-collapsed');
      desc.classList.remove('is-expanded');
      toggle.hidden = true;
      toggle.textContent = 'Mehr lesen';
      toggle.onclick = () => this._toggleGiftDescription(desc, toggle);
      requestAnimationFrame(() => {
        if (this._giftDescriptionOverflows(desc)) {
          toggle.hidden = false;
        } else {
          desc.classList.remove('is-collapsed');
        }
      });
    }

    _getConfigurableGiftItems() {
      return this._getGiftItems().filter((item) => {
        if (this._isGiftOptIn(item)) return true;
        return item.variants?.length > 1;
      });
    }

    _isGiftOptIn(item) {
      if (!item) return false;
      if (item.optIn) return true;
      return item.slot === 1 && this.gift1OptIn;
    }

    _getGiftDeclineLabel(item) {
      if (this._isGiftOptIn(item)) {
        return item.declineLabel || this.gift1DeclineLabel;
      }
      return this.giftDeclineLabel;
    }

    _getGiftAcceptLabel(item) {
      return item?.acceptLabel || this.gift1AcceptLabel;
    }

    _getGiftDrawerTitle(item) {
      if (item?.drawerTitle) return item.drawerTitle;
      if (this._isGiftOptIn(item)) {
        return this.gift1DrawerTitle;
      }
      return item?.title || '';
    }

    _isGiftOptInDeclined(slot) {
      return Boolean(this.giftOptInDeclined[slot]);
    }

    _isGiftOptional(item) {
      if (!item || !this.giftOptional) return false;
      if (this.gift1OptIn) return false;
      return true;
    }

    _createOptInDeclineRow(item, checked) {
      const row = document.createElement('label');
      row.className =
        'bk-media-bundle-builder__drawer-item bk-media-bundle-builder__colors-variant-row bk-media-bundle-builder__colors-variant-row--opt-in-decline';
      row.setAttribute('data-colors-variant-row', '');
      row.setAttribute('data-gift-slot', String(item.slot));
      row.setAttribute('data-opt-in-decline', 'true');
      if (checked) row.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `gift-colors-${item.slot}`;
      input.value = 'decline';
      input.checked = checked;

      const info = document.createElement('div');
      info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';

      const title = document.createElement('span');
      title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
      title.textContent = this._getGiftDeclineLabel(item);
      info.appendChild(title);

      row.appendChild(input);
      row.appendChild(info);
      return row;
    }

    _createOptInAcceptRow(item, variant, checked) {
      const row = document.createElement('label');
      row.className = 'bk-media-bundle-builder__drawer-item bk-media-bundle-builder__colors-variant-row';
      row.setAttribute('data-colors-variant-row', '');
      row.setAttribute('data-gift-slot', String(item.slot));
      row.setAttribute('data-variant-id', String(variant.id));
      if (!variant.available) row.classList.add('is-unavailable');
      if (checked) row.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `gift-colors-${item.slot}`;
      input.value = String(variant.id);
      input.checked = checked;
      input.disabled = !variant.available;

      const imageWrap = document.createElement('div');
      imageWrap.className = 'bk-media-bundle-builder__drawer-item-image';
      const imageUrl = this._getGiftVariantImage(variant, item) || item.fallbackImage;
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 56;
        img.height = 56;
        imageWrap.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';

      const title = document.createElement('span');
      title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
      title.textContent = this._getGiftAcceptLabel(item);
      info.appendChild(title);

      if (!variant.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'text-xs bk-media-bundle-builder__colors-variant-sold-out';
        soldOut.textContent = 'Ausverkauft';
        info.appendChild(soldOut);
      }

      row.appendChild(input);
      row.appendChild(imageWrap);
      row.appendChild(info);
      return row;
    }

    _createColorsVariantRow(item, variant, checked) {
      const row = document.createElement('label');
      row.className = 'bk-media-bundle-builder__drawer-item bk-media-bundle-builder__colors-variant-row';
      row.setAttribute('data-colors-variant-row', '');
      row.setAttribute('data-gift-slot', String(item.slot));
      row.setAttribute('data-variant-id', String(variant.id));
      if (!variant.available) row.classList.add('is-unavailable');
      if (checked) row.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `gift-colors-${item.slot}`;
      input.value = String(variant.id);
      input.checked = checked;
      input.disabled = !variant.available;

      const imageWrap = document.createElement('div');
      imageWrap.className = 'bk-media-bundle-builder__drawer-item-image';
      const imageUrl = this._getGiftVariantImage(variant, item) || item.fallbackImage;
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 56;
        img.height = 56;
        imageWrap.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';

      const title = document.createElement('span');
      title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
      title.textContent = variant.title;
      info.appendChild(title);

      if (!variant.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'text-xs bk-media-bundle-builder__colors-variant-sold-out';
        soldOut.textContent = 'Ausverkauft';
        info.appendChild(soldOut);
      }

      row.appendChild(input);
      row.appendChild(imageWrap);
      row.appendChild(info);
      return row;
    }

    _renderColorsDrawerContent(item) {
      const selectedId = this.giftSelections[item.slot];
      const isOptIn = this._isGiftOptIn(item);

      if (isOptIn) {
        const declined = selectedId ? false : this._isGiftOptInDeclined(item.slot) !== false;
        this.colorsDrawerList.appendChild(
          this._createOptInDeclineRow(item, declined || !selectedId)
        );

        if (item.variants?.length === 1) {
          const variant = item.variants[0];
          if (variant && (variant.available || this._shouldShowSoldOutGiftVariants(item))) {
            this.colorsDrawerList.appendChild(
              this._createOptInAcceptRow(item, variant, Boolean(selectedId))
            );
          }
        } else {
          this._getVisibleGiftVariants(item).forEach((variant) => {
            const checked = selectedId ? String(selectedId) === String(variant.id) : false;
            this.colorsDrawerList.appendChild(this._createColorsVariantRow(item, variant, checked));
          });
        }
        return;
      }

      this._getVisibleGiftVariants(item).forEach((variant) => {
        const checked = selectedId ? String(selectedId) === String(variant.id) : false;
        this.colorsDrawerList.appendChild(this._createColorsVariantRow(item, variant, checked));
      });
    }

    _getColorsDrawerCheckedInput(item) {
      return this.colorsDrawerList?.querySelector(`input[name="gift-colors-${item.slot}"]:checked`);
    }

    _getColorsDrawerSelectedVariant(item) {
      const checked = this._getColorsDrawerCheckedInput(item);
      if (!checked || checked.value === 'decline') return null;
      return item.variants?.find((variant) => String(variant.id) === checked.value) || null;
    }

    _persistColorsDrawerSelection(slot) {
      const item = this._getGiftItem(slot);
      if (!item) return false;

      const checked = this._getColorsDrawerCheckedInput(item);
      if (!checked) return false;

      if (this._isGiftOptIn(item) && checked.value === 'decline') {
        const changed = Boolean(this.giftSelections[slot]) || !this._isGiftOptInDeclined(slot);
        delete this.giftSelections[slot];
        this.giftOptInDeclined[slot] = true;
        this._updateGiftCardUi(slot, null, item);
        this._updateColorsSlotUi();
        this._updateUi();
        this._updateDrawerPricing();
        return changed;
      }

      const variant = this._getColorsDrawerSelectedVariant(item);
      if (!variant?.available) return false;

      const changed = this.giftSelections[slot] !== variant.id;
      this.giftSelections[slot] = variant.id;
      if (this._isGiftOptIn(item)) {
        this.giftOptInDeclined[slot] = false;
      }
      this._updateGiftCardUi(slot, variant, item);
      this._updateColorsSlotUi();
      this._updateUi();
      this._updateDrawerPricing();
      return changed;
    }

    _getColorsCelebrationContext() {
      return {
        goalToast: this.colorsDrawerGoalToast,
        celebration: this.colorsDrawerCelebration,
        confirm: this.colorsPickerConfirm,
        drawer: this.colorsDrawer,
        timeoutKey: '_colorsCelebrationTimeout'
      };
    }

    _getRecipeCelebrationContext() {
      return {
        goalToast: this.drawerGoalToast,
        celebration: this.drawerCelebration,
        confirm: this.drawerConfirm,
        drawer: this.drawer,
        timeoutKey: '_celebrationTimeout'
      };
    }

    _getAtcCelebrationContext() {
      return {
        goalToast: this.atcGoalToast,
        celebration: this.atcCelebration,
        confirm: this.atcButton,
        drawer: this,
        timeoutKey: '_atcCelebrationTimeout',
        message: this.atcSuccessMessage
      };
    }

    async _openColorsDrawer(slot) {
      if (!this.colorsDrawer) return;

      this.colorsDrawerList =
        this.colorsDrawer.querySelector('[data-colors-drawer-list]') || this.colorsDrawerList;
      if (!this.colorsDrawerList) return;
      this._bindColorsDrawerListEvents();

      const item = this._getGiftItem(slot);
      if (!item) return;
      if (!this._isGiftOptIn(item) && item.variants?.length <= 1) return;

      if (this.giftDrawer?.open) {
        this._closeGiftModal();
      }

      this._activePickerContext = 'colors';
      this._activeColorsSlot = slot;
      this.colorsDrawerList.innerHTML = '';
      this._renderColorsDrawerContent(item);

      const headerTitle = this.colorsDrawer.querySelector('[data-colors-drawer-header]');
      if (headerTitle) headerTitle.textContent = this._getGiftDrawerTitle(item);

      const confirmTitle = this.colorsPickerConfirm?.querySelector(
        '[data-colors-picker-confirm-title]'
      );
      if (confirmTitle) confirmTitle.textContent = this._getGiftDrawerTitle(item);

      const savedVariant = this._getSelectedGiftVariant(slot);
      const savedDeclined = this._isGiftOptIn(item) && this._isGiftOptInDeclined(slot);
      this._colorsDrawerLastCount = savedVariant?.available || savedDeclined ? 1 : 0;
      this._clearCelebration(this._getColorsCelebrationContext());

      this._syncColorsDrawerState();

      try {
        if (this.colorsDrawer.open) {
          this.colorsDrawer.removeAttribute('inert');
          this._syncColorsDrawerState();
          return;
        }
        await this.colorsDrawer.show?.();
      } catch (_error) {
        // Drawer show can reject if interrupted; still force interactive state.
      }

      this.colorsDrawer.removeAttribute('inert');
      this._syncColorsDrawerState();
    }

    _closeColorsDrawer() {
      if (!this.colorsDrawer) return;
      this.colorsDrawer.hide?.();
      this._clearCelebration(this._getColorsCelebrationContext());
      if (this.colorsPickerStatus) this.colorsPickerStatus.textContent = '';
      if (this.colorsPickerConfirm) this.colorsPickerConfirm.disabled = true;
    }

    _syncColorsDrawerState() {
      const slot = this._activeColorsSlot;
      const item = slot ? this._getGiftItem(slot) : null;
      if (!this.colorsDrawerList || !item) return;

      const checked = this._getColorsDrawerCheckedInput(item);
      const variant = this._getColorsDrawerSelectedVariant(item);
      const declined = this._isGiftOptIn(item) && checked?.value === 'decline';
      const selected = variant?.available || declined ? 1 : 0;
      const previousSelected = this._colorsDrawerLastCount ?? 0;
      let statusMessage = '';

      if (variant && !variant.available) {
        statusMessage = 'Diese Variante ist ausverkauft.';
      }

      if (selected === 1 && previousSelected < 1) {
        this._triggerCelebration(this._getColorsCelebrationContext());
      } else if (selected < 1) {
        this._clearCelebration(this._getColorsCelebrationContext());
      }

      this._colorsDrawerLastCount = selected;

      if (this.colorsPickerConfirmCount) {
        this.colorsPickerConfirmCount.textContent = `${selected}/1 ausgewählt`;
      }
      if (this.colorsPickerStatus) {
        this.colorsPickerStatus.textContent = statusMessage;
      }
      if (this.colorsPickerConfirm) {
        this.colorsPickerConfirm.disabled = selected !== 1;
      }

      this.colorsDrawerList
        .querySelectorAll('[data-colors-variant-row]')
        .forEach((row) => {
          const input = row.querySelector('input[type="radio"]');
          row.classList.toggle('is-selected', Boolean(input?.checked));
        });
    }

    _confirmColorsDrawer() {
      const slot = this._activeColorsSlot;
      if (!slot) return;

      const item = this._getGiftItem(slot);
      if (!item) return;

      const checked = this._getColorsDrawerCheckedInput(item);
      const declined = this._isGiftOptIn(item) && checked?.value === 'decline';
      const variant = this._getColorsDrawerSelectedVariant(item);
      if (!declined && !variant?.available) return;

      this._persistColorsDrawerSelection(slot);
      this._activeColorsSlot = null;
      this._activePickerContext = null;
      this._closeColorsDrawer();
    }

    _updateColorsSlotUi() {
      const slots = this.querySelectorAll('[data-colors-slot]');
      if (!slots.length) return;

      slots.forEach((button) => {
        const slot = parseInt(button.getAttribute('data-gift-slot'), 10);
        const item = this._getGiftItem(slot);
        const slotWrap = button.closest('[data-bundle-slot-wrap]');
        const applies = item ? this._giftAppliesToCurrentPreset(item) : false;

        if (slotWrap) {
          slotWrap.hidden = !applies;
        }
        if (!applies) return;

        const label = button.querySelector('[data-colors-slot-label]');
        const imageWrap = button.querySelector('[data-colors-slot-image]');
        const variant = this._getSelectedGiftVariant(slot);
        const isSingleVariant = (item?.variants?.length || 0) === 1 && !this._isGiftOptIn(item);
        const displayVariant =
          variant || (isSingleVariant && item.variants[0]?.available ? item.variants[0] : null);
        const hasSelection = Boolean(displayVariant);

        if (label) {
          if (hasSelection) {
            const displayLabel = this._getGiftVariantDisplayLabel(displayVariant);
            label.textContent = displayLabel;
            label.hidden = !displayLabel;
            if (displayLabel) {
              label.classList.remove('text-subdued');
            }
          } else if (this._isGiftOptIn(item)) {
            label.hidden = false;
            label.textContent = this._getGiftDeclineLabel(item);
            label.classList.add('text-subdued');
          } else if (this._isGiftOptional(item)) {
            label.hidden = false;
            label.textContent = this.giftDeclineLabel;
            label.classList.add('text-subdued');
          } else {
            label.hidden = false;
            label.textContent = this.colorsPlaceholder;
            label.classList.add('text-subdued');
          }
        }

        if (imageWrap && item) {
          const previewImage = hasSelection
            ? this._getGiftVariantImage(displayVariant, item)
            : item.fallbackImage;
          if (previewImage) {
            imageWrap.innerHTML = `<img src="${previewImage}" alt="" loading="lazy" width="60" height="60">`;
          }
        }

        button.classList.toggle('is-filled', hasSelection);

        const check = button.querySelector('[data-slot-check]');
        if (check) {
          check.setAttribute('aria-hidden', hasSelection ? 'false' : 'true');
        }

        const removeBtn = button
          .closest('[data-bundle-slot-wrap]')
          ?.querySelector('[data-gift-slot-remove]');
        if (removeBtn) {
          const singleVariant = (item?.variants?.length || 0) <= 1 && !this._isGiftOptIn(item);
          removeBtn.hidden = singleVariant || !hasSelection;
        }
      });

      this._updateKitGiftSlotUi();
    }

    _updateKitGiftSlotUi() {
      this.querySelectorAll('[data-bundle-kit-gift-trigger]').forEach((slotEl) => {
        const slot = parseInt(slotEl.getAttribute('data-gift-slot'), 10);
        const variant = this._getSelectedGiftVariant(slot);
        const hasSelection = Boolean(variant);

        slotEl.classList.toggle('is-filled', hasSelection);

        const variantLabel = slotEl.querySelector('[data-kit-gift-variant]');
        if (variantLabel) {
          const name = variant ? this._getGiftVariantDisplayLabel(variant) : '';
          variantLabel.textContent = name;
          variantLabel.hidden = !name;
        }

        const image = slotEl.querySelector('.bk-media-bundle-builder__kit-gift-image img');
        if (image && variant?.image) {
          image.src = variant.image;
          image.removeAttribute('srcset');
        }
      });
    }

    _getTopPresetQuantity() {
      const fromAttr = parseInt(this.getAttribute('data-gifts-top-preset-quantity'), 10);
      if (fromAttr) return fromAttr;
      const fromJson = parseInt(this.giftPricing?.topPresetQuantity, 10);
      if (fromJson) return fromJson;
      return Math.max(...this.presets.map((preset) => preset.quantity || 0), 0);
    }

    _shouldShowBaseGifts() {
      if (this._isDirectOfferSelected()) return false;
      if (this.giftMode === 'single_always') {
        return Boolean(this._getGiftItem(1));
      }
      if (this.flexibleSelection) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if (presetIndex === 3) return false;
        const effectiveQty = this._getPricingQuantity();
        const minQty = parseInt(this.giftPricing?.minQuantity, 10) || 4;
        return effectiveQty >= minQty;
      }
      const minQty = parseInt(this.giftPricing?.minQuantity, 10) || 4;
      return this._getRequiredQty() >= minQty;
    }

    _shouldShowGift3() {
      if (this.giftMode === 'single_always') return false;
      if (this.flexibleSelection) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if (presetIndex === 3) return false;
        const effectiveQty = this._getPricingQuantity();
        return effectiveQty >= this._getTopPresetQuantity();
      }
      return this._getRequiredQty() >= this._getTopPresetQuantity();
    }

    _isGiftProfiKitOnly(item) {
      if (!item) return false;
      if (item.profiKitOnly) return true;
      if (item.slot === 2) {
        return this.getAttribute('data-gift-2-profi-kit-only') === 'true';
      }
      if (item.slot === 4) {
        return this.getAttribute('data-gift-4-profi-kit-only') === 'true';
      }
      if (item.slot === 5) {
        return this.getAttribute('data-gift-5-profi-kit-only') === 'true';
      }
      return false;
    }

    _isGiftStarterProfiOnly(item) {
      if (!item) return false;
      if (this._isGiftProfiKitOnly(item)) return false;
      if (item.starterProfiOnly) return true;
      if (item.slot === 5) {
        return this.getAttribute('data-gift-5-starter-profi-only') === 'true';
      }
      return false;
    }

    _isStarterOrProfiPreset(presetIndex) {
      const index = parseInt(presetIndex, 10);
      return index === 1 || index === 2;
    }

    _giftAppliesToCurrentPreset(item) {
      if (!item) return false;
      if (item.separateCart) return true;
      if (this.giftMode === 'single_always') {
        return item.slot === 1;
      }
      if (this._isGiftOptIn(item)) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if (presetIndex === 1 && !this.gift1AllPresets) return false;
        return this._shouldShowBaseGifts();
      }
      if (item.slot === 3) {
        return this._shouldShowGift3();
      }
      if (this._isGiftProfiKitOnly(item)) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if ((item.slot === 4 || item.slot === 5) && presetIndex !== 2) return false;
        if (presetIndex === 1) return false;
        return this._shouldShowBaseGifts();
      }
      if (this._isGiftStarterProfiOnly(item)) {
        const presetIndex = parseInt(this.activePreset?.presetIndex, 10);
        if (!this._isStarterOrProfiPreset(presetIndex)) return false;
        return this._shouldShowBaseGifts();
      }
      return this._shouldShowBaseGifts();
    }

    _getApplicableGiftItems() {
      return this._getGiftItems().filter((item) => this._giftAppliesToCurrentPreset(item));
    }

    _clearInactiveGiftSelections() {
      this._getGiftItems().forEach((item) => {
        if (!this._giftAppliesToCurrentPreset(item)) {
          delete this.giftSelections[item.slot];
          if (this._isGiftOptIn(item)) {
            this.giftOptInDeclined[item.slot] = true;
          } else {
            delete this.giftOptInDeclined[item.slot];
          }
        }
      });
    }

    _syncApplicableSingleVariantGifts() {
      this._getApplicableGiftItems().forEach((item) => {
        if (this._isGiftOptIn(item)) return;
        if ((item.variants?.length || 0) !== 1) return;
        const variant = item.variants[0];
        if (!variant?.available) return;
        if (!this.giftSelections[item.slot]) {
          this.giftSelections[item.slot] = variant.id;
        }
      });
      this._getGiftItems().forEach((item) => {
        if (!item.separateCart || !this._giftAppliesToCurrentPreset(item)) return;
        if (this.giftSelections[item.slot]) return;
        const variant = (item.variants || []).find((entry) => entry.available);
        if (!variant) return;
        this.giftSelections[item.slot] = variant.id;
      });
    }

    _updateColorsVisibility() {
      const showsBaseGifts = this._shouldShowBaseGifts();
      const panels = [
        this.querySelector('[data-bundle-opt-in-panel]'),
        this.querySelector('[data-bundle-colors-panel]'),
        this.querySelector('[data-bundle-gifts-panel]')
      ].filter(Boolean);

      panels.forEach((panel) => {
        const visibleSlots = Array.from(panel.querySelectorAll('[data-colors-slot]')).filter(
          (button) => {
            const slot = parseInt(button.getAttribute('data-gift-slot'), 10);
            const item = this._getGiftItem(slot);
            return item && this._giftAppliesToCurrentPreset(item);
          }
        );
        panel.hidden = visibleSlots.length === 0;
      });

      if (this.colorsDrawer?.open) {
        const activeItem = this._activeColorsSlot ? this._getGiftItem(this._activeColorsSlot) : null;
        const pickingApplicableGift =
          activeItem &&
          this._giftAppliesToCurrentPreset(activeItem) &&
          !this._isDirectOfferSelected();
        if (!pickingApplicableGift && !showsBaseGifts) {
          this._closeColorsDrawer();
        }
      }
    }

    _openGiftModalForSlot(slot) {
      const item = this._getGiftItem(slot);
      if (!item || !this.giftDrawer || !this.giftDrawerContent) return;

      const blockId = this.getAttribute('data-block-id');
      const template = document.getElementById(`bundle-gift-content-${blockId}-${slot}`);
      if (!template) return;

      this.giftDrawerContent.innerHTML = '';
      this.giftDrawerContent.appendChild(template.content.cloneNode(true));

      if (this.giftDrawerHeader) {
        const title = item.title || '';
        if (this.includeGiftsInBundle) {
          this.giftDrawerHeader.textContent = title.replace(/^GRATIS:\s*/i, '');
        } else {
          this.giftDrawerHeader.textContent = title.toUpperCase().includes('GRATIS')
            ? title
            : `GRATIS: ${title}`;
        }
      }

      this._initGiftModalDescription();

      if (this.giftDrawerFooter) {
        this.giftDrawerFooter.hidden = true;
      }

      this.giftDrawer.show?.();
    }

    _closeGiftModal() {
      if (!this.giftDrawer) return;
      this.giftDrawer.hide?.();
      this._activeGiftPickerSlot = null;
      this._activePickerContext = null;
      if (this.giftDrawerFooter) this.giftDrawerFooter.hidden = true;
      if (this.giftPickerStatus) this.giftPickerStatus.textContent = '';
      if (this.giftPickerConfirm) this.giftPickerConfirm.disabled = true;
    }

    _giftDescriptionOverflows(element) {
      const styles = getComputedStyle(element);
      const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.6;
      const maxHeight = lineHeight * 5;
      return element.scrollHeight > maxHeight + 2;
    }

    _toggleGiftDescription(desc, toggle) {
      const expanded = desc.classList.toggle('is-expanded');
      desc.classList.toggle('is-collapsed', !expanded);
      toggle.textContent = expanded ? 'Weniger lesen' : 'Mehr lesen';
    }

    _initFromForm() {
      const variant = this._getCurrentVariant();
      if (!variant) return;
      this._updateKitRowVariants(variant);
      this._applyPresetForKit(getVariantOptions(variant)[this.kitOptionIndex]);
      this._syncKitRows(variant);
      this._renderSlots();
      this._updateKitRowPricing();
      this._updateUi();
      this._updateDrawerPricing();
      if (this.flexibleSelection) {
        this._updateFlexibleProgressBanner();
      }
      this._applyPreselectedPreset();
      this._applyPrefillFromUrl();
      this._applyInitialDirectOffer();
    }

    _applyPreselectedPreset() {
      const index = this.preselectPresetIndex;
      if (!index) return;

      const params = new URLSearchParams(window.location.search);
      if (params.has('variant') || params.has('bb_kit_variant')) return;

      const row = this.querySelector(`[data-bundle-kit-row][data-preset-index="${index}"]`);
      if (!row || row.classList.contains('is-selected')) return;
      this._selectKit(row);
    }

    _applyPrefillFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const kitVariantId = parseInt(params.get('bb_kit_variant'), 10);

      if (kitVariantId) {
        const kitRow = this.querySelector(
          `[data-bundle-kit-row][data-variant-id="${kitVariantId}"]`
        );
        if (kitRow) {
          this._selectKit(kitRow);
        } else {
          const kitVariant = this.variants.find((v) => v.id === kitVariantId);
          if (kitVariant) {
            this._setFormVariantId(kitVariantId);
            this._syncGalleryWithoutPicker(kitVariant);
            this._updateKitRowVariants(kitVariant);
            const kitValue = getVariantOptions(kitVariant)[this.kitOptionIndex];
            this._applyPresetForKit(kitValue);
            this._syncKitRows(kitVariant);
            this._renderSlots();
            this._updateKitRowPricing();
            this._updateUi();
            this._updateDrawerPricing();
          }
        }
      }

      const variantId = parseInt(params.get('bb_variant'), 10);
      const productId = parseInt(params.get('bb_product'), 10);
      if (!variantId && !productId) return;

      let product =
        (variantId &&
          this.products.find((entry) => Number(entry.variant_id) === variantId)) ||
        (productId && this.products.find((entry) => Number(entry.id) === productId)) ||
        null;

      if (!product || !this._isProductAvailable(product)) return;

      const selection = {
        variantId: Number(product.variant_id),
        title: formatDrawerProductTitle(product.title || ''),
        image: product.image || null
      };

      this.selections = [selection];
      this._syncDrawerQuantitiesFromSelections();

      const kitValue = this.activePreset?.kitValue;
      if (kitValue) {
        this.presetSelections[kitValue] = [selection];
      }

      this._renderSlots();
      this._updateUi();
      this._updateDrawerPricing();
      if (this.flexibleSelection) {
        this._updateFlexibleProgressBanner();
      } else if (this.optionalExtraSlot) {
        this._updateOptionalProgressBanner();
      }

    }

    _getCurrentVariant() {
      const form = this.form;
      if (!form) return this.variants[0] || null;
      const idInput = form.querySelector('input[name="id"]');
      const id = idInput ? parseInt(idInput.value, 10) : null;
      return this.variants.find((v) => v.id === id) || this.variants[0] || null;
    }

    _onVariantChange(event) {
      if (
        this._suppressVariantChangeHandling ||
        this._kitRevertInProgress ||
        this._kitSelectInProgress ||
        this._flexibleUpgradeInProgress ||
        this._isDirectOfferSelected()
      ) {
        return;
      }
      const variant = event.detail?.variant;
      if (!variant) return;
      const full = this.variants.find((v) => v.id === variant.id) || variant;
      const kitValue = getVariantOptions(full)[this.kitOptionIndex];
      if (!this._canSwitchToKit(kitValue)) {
        this._revertToActiveKit();
        return;
      }
      this._syncKitRows(full);
      this._updateKitRowVariants(full);
      this._updateKitRowPricing();
      this._updateUi();
      this._updateDrawerPricing();
    }

    _applyPresetForKit(kitValue, presetIndex) {
      const index = parseInt(presetIndex, 10);
      let preset =
        this.presets.find((p) => p.kitValue === kitValue) ||
        (index ? this.presets.find((p) => p.presetIndex === index) : null);

      if (!preset && kitValue) {
        const row = this.querySelector(
          `[data-bundle-kit-row][data-kit-value="${CSS.escape(kitValue)}"]`
        );
        if (row) {
          preset = {
            kitValue,
            presetIndex: parseInt(row.getAttribute('data-preset-index'), 10) || index,
            quantity: parseInt(row.getAttribute('data-preset-quantity'), 10) || 1,
            discountPercent: 0,
            bundleGroup: row.getAttribute('data-bundle-group') || null
          };
        }
      }

      this.activePreset = preset || { quantity: 1, discountPercent: 0, kitValue };
    }

    _trimSelectionsToRequired() {
      if (this._hasOptionalExtraSlots()) return;
      const required = this._getRequiredQty();
      if (this.selections.length > required) {
        this.selections = this.selections.slice(0, required);
      }
    }

    _getPresetQuantityForKitValue(kitValue) {
      if (!kitValue) return 0;
      const preset = this.presets.find((p) => p.kitValue === kitValue);
      if (preset?.quantity) return preset.quantity;
      const row = this.querySelector(
        `[data-bundle-kit-row][data-kit-value="${CSS.escape(kitValue)}"]`
      );
      return parseInt(row?.getAttribute('data-preset-quantity'), 10) || 0;
    }

    _getPresetIndexForKitValue(kitValue) {
      if (!kitValue) return null;
      const preset = this.presets.find((p) => p.kitValue === kitValue);
      if (preset?.presetIndex) return preset.presetIndex;
      const row = this.querySelector(
        `[data-bundle-kit-row][data-kit-value="${CSS.escape(kitValue)}"]`
      );
      const presetIndex = parseInt(row?.getAttribute('data-preset-index'), 10);
      return Number.isFinite(presetIndex) ? presetIndex : null;
    }

    _isFlexibleKitSwitchBlocked(currentPresetIndex, targetPresetIndex, selectionCount) {
      if (!selectionCount || !currentPresetIndex || !targetPresetIndex) return false;
      if (currentPresetIndex === targetPresetIndex) return false;

      if (targetPresetIndex === 3 && (currentPresetIndex === 1 || currentPresetIndex === 2)) {
        return true;
      }

      if (currentPresetIndex === 2 && targetPresetIndex === 1) {
        const starterRow = this.querySelector('[data-bundle-kit-row][data-preset-index="1"]');
        const starterMax = parseInt(starterRow?.getAttribute('data-preset-quantity'), 10) || 4;
        if (selectionCount > starterMax) return true;
      }

      return false;
    }

    _getIncomingSelections(previousKit) {
      const fromDrawer = this._flattenDrawerQuantitiesToSelections();
      if (fromDrawer.length > 0) return fromDrawer;
      if (this.selections.length > 0) {
        return [...this.selections];
      }
      if (previousKit && this.presetSelections[previousKit]?.length) {
        return [...this.presetSelections[previousKit]];
      }
      return [];
    }

    _flattenDrawerQuantitiesToSelections() {
      const total = this._getDrawerTotalQty();
      if (total <= 0) return [];

      const flat = [];
      const seenProductIds = new Set();
      Object.entries(this.drawerQuantities).forEach(([variantId, qty]) => {
        const product = this.products.find((p) => String(p.variant_id) === variantId);
        if (!this._isProductAvailable(product)) return;
        const productKey = product?.id || variantId;
        if (this.limitOnePerProduct) {
          if (seenProductIds.has(productKey)) return;
          seenProductIds.add(productKey);
        }
        const take = this.limitOnePerProduct ? Math.min(qty, 1) : qty;
        for (let i = 0; i < take; i++) {
          flat.push({
            variantId: parseInt(variantId, 10),
            title: formatDrawerProductTitle(product?.title || ''),
            image: product?.image || null
          });
        }
      });
      return flat;
    }

    _shouldPreserveSelectionsOnKitSwitch(previousPresetIndex, targetPresetIndex, selectionCount) {
      if (!selectionCount) return false;
      if (
        this.flexibleSelection &&
        this._isFlexibleKitSwitchBlocked(previousPresetIndex, targetPresetIndex, selectionCount)
      ) {
        return false;
      }
      if (this.flexibleSelection && previousPresetIndex === 1 && targetPresetIndex === 2) {
        return true;
      }
      return selectionCount > 0;
    }

    _getCurrentSelectionCount() {
      if (this.drawer?.open && this._getDrawerTotalQty() > 0) {
        return this._getDrawerTotalQty();
      }
      return this.selections.length;
    }

    _canSwitchToKit(targetKitValue) {
      if (this.autoSelectSingleProduct) return true;
      const currentKit = this.activePreset?.kitValue;
      if (!targetKitValue || !currentKit || currentKit === targetKitValue) return true;

      const selectionCount = this._getCurrentSelectionCount();

      if (this.flexibleSelection) {
        if (selectionCount <= 0) return true;
        const currentPresetIndex = parseInt(this.activePreset?.presetIndex, 10);
        const targetPresetIndex = this._getPresetIndexForKitValue(targetKitValue);
        return !this._isFlexibleKitSwitchBlocked(
          currentPresetIndex,
          targetPresetIndex,
          selectionCount
        );
      }

      if (this.optionalExtraSlot) return true;

      const targetQty = this._getPresetQuantityForKitValue(targetKitValue);
      if (!targetQty) return true;

      return selectionCount <= targetQty;
    }

    _revertToActiveKit() {
      const kitValue = this.activePreset?.kitValue;
      if (!kitValue) return;
      const row = this.querySelector(
        `[data-bundle-kit-row][data-kit-value="${CSS.escape(kitValue)}"]`
      );
      if (!row) return;

      this._kitRevertInProgress = true;
      try {
        const variantId = row.getAttribute('data-variant-id');
        if (variantId) {
          this._setFormVariantId(variantId);
        }
        this._setKitOptionInForm(row.getAttribute('data-option-value-id'));
        this._syncGalleryForKitRow(row);
        this._highlightMissing(row);
      } finally {
        this._kitRevertInProgress = false;
      }
    }

    _updateKitRowSwitchability() {
      this.querySelectorAll('[data-bundle-kit-row]').forEach((row) => {
        const kitValue = row.getAttribute('data-kit-value');
        const isSelected = row.classList.contains('is-selected');
        const allowed = this._canSwitchToKit(kitValue);
        row.classList.toggle('is-switch-disabled', !allowed && !isSelected);
        if (!allowed && !isSelected) {
          row.setAttribute('aria-disabled', 'true');
        } else {
          row.removeAttribute('aria-disabled');
        }
      });
    }

    _removeSelectionAtIndex(index) {
      if (index < 0 || index >= this.selections.length) return;
      this.selections.splice(index, 1);
      this._saveSelectionsForPreset(this.activePreset?.kitValue);
      this._renderSlots();
      if (this.flexibleSelection) {
        this._syncFlexibleAfterSelectionChange();
        return;
      }
      if (this.optionalExtraSlot) {
        this._syncOptionalAfterSelectionChange();
        return;
      }
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      this._updateUi();
      this._updateDrawerPricing();
    }

    _removeGiftSelection(slot) {
      const item = this._getGiftItem(slot);
      if (!item) return;
      if (this._isGiftOptIn(item)) {
        delete this.giftSelections[slot];
        this.giftOptInDeclined[slot] = true;
        this._updateGiftCardUi(slot, null, item);
        this._updateColorsSlotUi();
        this._updateKitRowPricing();
        this._updateUi();
        return;
      }
      if (!this.giftSelections[slot]) return;
      delete this.giftSelections[slot];
      this._updateGiftCardUi(slot, null, item);
      this._updateColorsSlotUi();
      this._updateKitRowPricing();
      this._updateUi();
    }

    _saveSelectionsForPreset(kitValue) {
      if (!kitValue) return;
      this.presetSelections[kitValue] = [...this.selections];
    }

    _applyDrawerQuantitiesToSelections({ syncEmpty = true } = {}) {
      const flat = this._flattenDrawerQuantitiesToSelections();
      if (flat.length === 0) {
        if (syncEmpty) this.selections = [];
        return;
      }
      this.selections = flat;
    }

    _persistDrawerSelections() {
      this._applyDrawerQuantitiesToSelections();
      this._saveSelectionsForPreset(this.activePreset?.kitValue);
      this._renderSlots();
      if (this.flexibleSelection) {
        this._syncFlexibleAfterSelectionChange();
      } else if (this.optionalExtraSlot) {
        this._syncOptionalAfterSelectionChange();
      } else {
        this._updateKitRowPricing();
        this._updateKitRowSwitchability();
        this._updateUi();
      }
    }

    _loadSelectionsForPreset(kitValue, previousKit, incomingSelections) {
      const saved = this.presetSelections[kitValue];
      const fromPrevious =
        previousKit &&
        previousKit !== kitValue &&
        this.presetSelections[previousKit]?.length
          ? [...this.presetSelections[previousKit]]
          : null;
      const incoming = incomingSelections?.length ? incomingSelections : null;

      if (incoming?.length) {
        this.selections = this._sanitizeSelections(incoming);
      } else if (saved?.length) {
        this.selections = this._sanitizeSelections(saved);
      } else if (fromPrevious) {
        this.selections = this._sanitizeSelections(fromPrevious);
      } else {
        this.selections = saved ? this._sanitizeSelections(saved) : [];
      }
      this._trimSelectionsToRequired();
    }

    _switchPreset(kitValue, presetIndex, options = {}) {
      const keepDrawerOpen = options.keepDrawerOpen === true;
      const previousKit = this.activePreset?.kitValue;
      const previousPresetIndex = this._getPresetIndexForKitValue(previousKit);
      const targetPresetIndex = this._getPresetIndexForKitValue(kitValue);

      const incomingSelections = this._getIncomingSelections(previousKit);

      if (previousKit && previousKit !== kitValue) {
        if (this._getDrawerTotalQty() > 0) {
          this._applyDrawerQuantitiesToSelections({ syncEmpty: false });
        } else if (incomingSelections.length > 0) {
          this.selections = this._sanitizeSelections(incomingSelections);
        }
        this._saveSelectionsForPreset(previousKit);
      }
      this._applyPresetForKit(kitValue, presetIndex);
      if (!kitValue) return;
      if (previousKit !== kitValue) {
        if (this.drawer?.open && !keepDrawerOpen) {
          this._suppressDrawerPersist = true;
          this.drawer.hide?.();
        }
        if (!keepDrawerOpen) {
          this.drawerQuantities = {};
        }

        const preserveSelections =
          options.preserveSelections ??
          this._shouldPreserveSelectionsOnKitSwitch(
            previousPresetIndex,
            targetPresetIndex,
            incomingSelections.length
          );

        if (preserveSelections && incomingSelections.length) {
          this.selections = this._sanitizeSelections(incomingSelections);
          const maxQty = this._getMaxSelectableQtyForKitValue(kitValue, targetPresetIndex);
          if (this.selections.length > maxQty) {
            this.selections = this.selections.slice(0, maxQty);
          }
          this._saveSelectionsForPreset(kitValue);
        } else {
          this._loadSelectionsForPreset(kitValue, previousKit, incomingSelections);
        }

        if (keepDrawerOpen && this.drawer?.open) {
          this._syncDrawerQuantitiesFromSelections();
          this._clearCelebration(this._getRecipeCelebrationContext());
        }
      } else {
        this._trimSelectionsToRequired();
      }
      this._fillSingleProductSelection();
    }

    _getSingleBundleProduct() {
      const available = (this.products || []).filter((product) => this._isProductAvailable(product));
      if (available.length !== 1) return null;
      return available[0];
    }

    _fillSingleProductSelection() {
      if (!this.autoSelectSingleProduct || this._isDirectOfferSelected()) return;
      const product = this._getSingleBundleProduct();
      if (!product) return;
      const qty = this._getRequiredQty();
      if (!qty) return;
      const selection = {
        variantId: Number(product.variant_id),
        title: formatDrawerProductTitle(product.title || ''),
        image: product.image || null
      };
      this.selections = Array.from({ length: qty }, () => ({ ...selection }));
      const kitValue = this.activePreset?.kitValue;
      if (kitValue) {
        this.presetSelections[kitValue] = this.selections.map((item) => ({ ...item }));
      }
    }

    _isDirectOfferSelected() {
      return Boolean(this.querySelector('[data-bundle-kit-row][data-direct-offer].is-selected'));
    }

    _selectDirectOffer(row) {
      this._kitSelectInProgress = true;
      try {
        this.querySelectorAll('[data-bundle-kit-row]').forEach((r) => {
          const selected = r === row;
          r.classList.toggle('is-selected', selected);
          r.toggleAttribute('aria-current', selected);
        });
        if (this.drawer?.open) this.drawer.hide?.();
        this._showDirectOfferGallery(row);
        this._updateUi();
      } finally {
        this._kitSelectInProgress = false;
      }
    }

    _getMainGalleryImage() {
      const gallery = document.querySelector(`product-gallery[form="${this.formId}"]`);
      if (!gallery) return null;
      return (
        gallery.querySelector('.product-gallery__media.is-selected:not([hidden]) img') ||
        gallery.querySelector('.product-gallery__media:not([hidden]) img')
      );
    }

    _showDirectOfferGallery(row) {
      const imageUrl = row?.getAttribute('data-gallery-image');
      if (!imageUrl) return;
      const img = this._directGalleryImage?.img || this._getMainGalleryImage();
      if (!img) return;

      if (!this._directGalleryImage) {
        this._directGalleryImage = {
          img,
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          alt: img.getAttribute('alt'),
          width: img.getAttribute('width'),
          height: img.getAttribute('height')
        };
      }

      img.src = imageUrl;
      const srcset = row.getAttribute('data-gallery-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      const alt = row.getAttribute('data-gallery-alt');
      if (alt) img.setAttribute('alt', alt);
      const width = row.getAttribute('data-gallery-width');
      const height = row.getAttribute('data-gallery-height');
      if (width) img.setAttribute('width', width);
      if (height) img.setAttribute('height', height);
    }

    _restoreDirectOfferGallery() {
      const state = this._directGalleryImage;
      if (!state?.img) return;
      const img = state.img;
      if (state.src != null) img.setAttribute('src', state.src);
      if (state.srcset != null) img.setAttribute('srcset', state.srcset);
      else img.removeAttribute('srcset');
      if (state.alt != null) img.setAttribute('alt', state.alt);
      if (state.width != null) img.setAttribute('width', state.width);
      if (state.height != null) img.setAttribute('height', state.height);
      this._directGalleryImage = null;
    }

    _syncDirectOfferUi() {
      const step = this.querySelector('[data-bundle-cards-step]');
      if (!step) return;
      step.hidden = this._isDirectOfferSelected() || this.autoSelectSingleProduct;
    }

    _selectKit(row, options = {}) {
      if (row?.hasAttribute('data-direct-offer')) {
        this._selectDirectOffer(row);
        return;
      }
      this._restoreDirectOfferGallery();
      const kitValue = row.getAttribute('data-kit-value');
      const optionValueId = row.getAttribute('data-option-value-id');
      const presetIndex = row.getAttribute('data-preset-index');
      const variantId = row.getAttribute('data-variant-id');
      const keepDrawerOpen = options.keepDrawerOpen === true;
      const currentPresetIndex = parseInt(this.activePreset?.presetIndex, 10);
      if (parseInt(presetIndex, 10) !== currentPresetIndex) {
        this._flexibleAutoUpgraded = false;
      }

      this._kitSelectInProgress = true;
      try {
        this.querySelectorAll('[data-bundle-kit-row]').forEach((r) => {
          r.classList.toggle('is-selected', r === row);
          r.toggleAttribute('aria-current', r === row);
        });

        if (variantId) {
          this._setFormVariantId(variantId);
        }
        this._setKitOptionInForm(optionValueId);
        this._syncGalleryForKitRow(row);
        this._switchPreset(kitValue, presetIndex, { keepDrawerOpen });

        this._renderSlots();
        this._updateKitRowPricing();
        this._updateKitRowSwitchability();
        this._updateUi();
        this._updateDrawerPricing();
        if (keepDrawerOpen) {
          this._ensureDrawerUpgradeProgressRefs();
          this._syncDrawerQtyDisplay();
        }
        if (this.flexibleSelection) {
          this._updateFlexibleProgressBanner();
        }
      } finally {
        this._kitSelectInProgress = false;
      }
    }

    _setKitOptionInForm(optionValueId) {
      const picker = this.variantPicker;
      const form = this.form;
      if (!picker || !form) return;

      const input = picker.querySelector(
        `input[data-option-position="${this.kitOptionPosition}"][value="${optionValueId}"]`
      );
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _syncKitRows(variant) {
      if (this._isDirectOfferSelected()) return;
      const kitValue = getVariantOptions(variant)[this.kitOptionIndex];
      this.querySelectorAll('[data-bundle-kit-row]').forEach((row) => {
        if (row.hasAttribute('data-direct-offer')) return;
        const selected = row.getAttribute('data-kit-value') === kitValue;
        row.classList.toggle('is-selected', selected);
        row.toggleAttribute('aria-current', selected);
      });

      if (this._kitSelectInProgress) return;

      const previousKit = this.activePreset?.kitValue;
      const previousPresetIndex = this._getPresetIndexForKitValue(previousKit);
      const targetPresetIndex = this._getPresetIndexForKitValue(kitValue);

      if (
        previousPresetIndex &&
        targetPresetIndex &&
        previousPresetIndex === targetPresetIndex &&
        previousKit !== kitValue
      ) {
        this._applyPresetForKit(kitValue, targetPresetIndex);
        this._renderSlots();
        this._updateKitRowPricing();
        this._updateKitRowSwitchability();
        this._updateUi();
        this._updateDrawerPricing();
        return;
      }

      this._switchPreset(kitValue);
      this._renderSlots();
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      this._updateUi();
      this._updateDrawerPricing();
    }

    _updateKitRowVariants(referenceVariant) {
      this.querySelectorAll('[data-bundle-kit-row]').forEach((row) => {
        if (row.hasAttribute('data-direct-offer')) return;
        const kitValue = row.getAttribute('data-kit-value');
        const match = this._findVariantForKitValue(kitValue, referenceVariant);
        if (match) {
          this._setKitRowVariantAttributes(row, match);
        }
      });
    }

    _findVariantForKitValue(kitValue, referenceVariant) {
      if (!kitValue) return null;
      const refOptions = referenceVariant ? getVariantOptions(referenceVariant) : [];
      const strictMatch = this.variants.find((v) => {
        const vOptions = getVariantOptions(v);
        if (vOptions[this.kitOptionIndex] !== kitValue) return false;
        if (refOptions.length <= 1) return true;
        return vOptions.every((opt, i) => {
          if (i === this.kitOptionIndex) return true;
          return opt === refOptions[i];
        });
      });
      return strictMatch || this.variants.find((v) => getVariantOptions(v)[this.kitOptionIndex] === kitValue) || null;
    }

    _renderSlots() {
      if (!this.slotsEl || !this.slotTemplate) return;
      if (this._hasOptionalExtraSlots()) {
        this._renderFlexibleSlots();
        return;
      }
      const qty = parseInt(this.activePreset?.quantity, 10) || 1;
      const placeholderLabel =
        this.slotTemplate.content.querySelector('[data-slot-label]')?.textContent?.trim() ||
        'Rezeptkarten auswählen...';
      const placeholderImageHtml =
        this.slotTemplate.content.querySelector('[data-slot-image]')?.innerHTML || '';

      this.slotsEl.innerHTML = '';
      for (let i = 0; i < qty; i++) {
        const node = this.slotTemplate.content.cloneNode(true);
        const btn = node.querySelector('[data-bundle-open-drawer]');
        const removeBtn = node.querySelector('[data-slot-remove]');
        const sel = this.selections[i];
        const check = btn?.querySelector('[data-slot-check]');
        if (sel && btn) {
          const label = btn.querySelector('[data-slot-label]');
          const imgWrap = btn.querySelector('[data-slot-image]');
          if (label) {
            label.textContent = sel.title;
            label.classList.remove('text-subdued');
          }
          if (imgWrap && sel.image) {
            imgWrap.innerHTML = `<img src="${sel.image}" alt="" loading="lazy" width="60" height="60">`;
          }
          btn.classList.add('is-filled');
          if (check) check.setAttribute('aria-hidden', 'false');
          if (removeBtn) {
            removeBtn.hidden = false;
            removeBtn.setAttribute('data-slot-index', String(i));
          }
        } else if (btn) {
          const label = btn.querySelector('[data-slot-label]');
          const imgWrap = btn.querySelector('[data-slot-image]');
          if (label) {
            label.textContent = placeholderLabel;
            label.classList.add('text-subdued');
          }
          if (imgWrap && placeholderImageHtml) {
            imgWrap.innerHTML = placeholderImageHtml;
          }
          btn.classList.remove('is-filled');
          if (check) check.setAttribute('aria-hidden', 'true');
          if (removeBtn) {
            removeBtn.hidden = true;
            removeBtn.removeAttribute('data-slot-index');
          }
        }
        this._markGratisSlot(btn, this._isGratisSlot(i, qty));
        this.slotsEl.appendChild(node);
      }
      this._updateKitRowSwitchability();
    }

    _isGratisSlot(index, quantity) {
      const freeQty = parseInt(this.activePreset?.freeQuantity, 10) || 0;
      if (freeQty <= 0 || quantity <= 0) return false;
      return index >= quantity - freeQty;
    }

    _markGratisSlot(btn, isGratis) {
      if (!btn) return;
      const existing = btn.querySelector('[data-slot-gratis]');
      if (!isGratis) {
        existing?.remove();
        return;
      }
      if (existing) return;
      const badge = document.createElement('span');
      badge.className = 'badge bk-media-bundle-builder__slot-gratis';
      badge.setAttribute('data-slot-gratis', '');
      badge.textContent = 'GRATIS';
      btn.appendChild(badge);
    }

    _renderFlexibleSlots() {
      const baseQty = this._getBaseRequiredQty();
      const maxQty = this._getMaxSelectableQty();
      if (this.selections.length > maxQty) {
        this.selections = this.selections.slice(0, maxQty);
      }
      const placeholderLabel =
        this.slotTemplate.content.querySelector('[data-slot-label]')?.textContent?.trim() ||
        'Rezeptkarten auswählen...';
      const placeholderImageHtml =
        this.slotTemplate.content.querySelector('[data-slot-image]')?.innerHTML || '';
      const filledCount = this.selections.length;
      const totalSlots = Math.max(baseQty, filledCount);

      this.slotsEl.innerHTML = '';

      for (let i = 0; i < totalSlots; i++) {
        const node = this.slotTemplate.content.cloneNode(true);
        const btn = node.querySelector('[data-bundle-open-drawer]');
        const removeBtn = node.querySelector('[data-slot-remove]');
        const sel = this.selections[i];
        const check = btn?.querySelector('[data-slot-check]');

        if (sel && btn) {
          const label = btn.querySelector('[data-slot-label]');
          const imgWrap = btn.querySelector('[data-slot-image]');
          if (label) {
            label.textContent = sel.title;
            label.classList.remove('text-subdued');
          }
          if (imgWrap && sel.image) {
            imgWrap.innerHTML = `<img src="${sel.image}" alt="" loading="lazy" width="60" height="60">`;
          }
          btn.classList.add('is-filled');
          if (check) check.setAttribute('aria-hidden', 'false');
          if (removeBtn) {
            removeBtn.hidden = false;
            removeBtn.setAttribute('data-slot-index', String(i));
          }
        } else if (btn) {
          const label = btn.querySelector('[data-slot-label]');
          const imgWrap = btn.querySelector('[data-slot-image]');
          if (label) {
            label.textContent = placeholderLabel;
            label.classList.add('text-subdued');
          }
          if (imgWrap && placeholderImageHtml) {
            imgWrap.innerHTML = placeholderImageHtml;
          }
          btn.classList.remove('is-filled');
          if (check) check.setAttribute('aria-hidden', 'true');
          if (removeBtn) {
            removeBtn.hidden = true;
            removeBtn.removeAttribute('data-slot-index');
          }
        }

        this.slotsEl.appendChild(node);
      }

      this._updateKitRowSwitchability();
      this._updateFlexibleProgressBanner();
    }

    _isProductAvailable(product) {
      return Boolean(product?.available);
    }

    _isDrawerProductAvailable(product) {
      if (this.drawerShowAllSoldOut) return false;
      return this._isProductAvailable(product);
    }

    _shouldShowSoldOutDrawerProducts() {
      if (this.drawerShowAllSoldOut) return true;
      if (!this.products.length) return false;
      return this.products.every((product) => !this._isProductAvailable(product));
    }

    _sanitizeSelections(selections) {
      const seenProductIds = new Set();
      return (selections || []).filter((selection) => {
        const product = this.products.find((entry) => entry.variant_id === selection.variantId);
        if (!this._isProductAvailable(product)) return false;
        if (!this.limitOnePerProduct) return true;
        const productKey = product?.id || selection.variantId;
        if (seenProductIds.has(productKey)) return false;
        seenProductIds.add(productKey);
        return true;
      });
    }

    _getDrawerProductsSorted() {
      const standard = this.defaultUnitPrice;

      return [...this.products].sort((a, b) => {
        const aAvailable = this._isProductAvailable(a);
        const bAvailable = this._isProductAvailable(b);
        if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;

        if (!standard) return 0;
        const aMatches = a.price === standard;
        const bMatches = b.price === standard;
        if (aMatches === bMatches) return 0;
        return aMatches ? -1 : 1;
      });
    }

    _renderDrawerProducts() {
      if (!this.drawerList) return;
      const qtyTemplate = this.querySelector('[data-bundle-qty-template]');
      const fragment = document.createDocumentFragment();

      const showSoldOut = this._shouldShowSoldOutDrawerProducts();

      this._getDrawerProductsSorted().forEach((product) => {
        const isUnavailable = !this._isDrawerProductAvailable(product);
        if (isUnavailable && !showSoldOut) return;
        const item = document.createElement('div');
        item.className = 'bk-media-bundle-builder__drawer-item';
        if (isUnavailable) item.classList.add('is-unavailable');
        if (product.rowBackground) {
          item.style.backgroundColor = product.rowBackground;
        }
        item.setAttribute('data-drawer-item', '');
        item.setAttribute('data-variant-id', String(product.variant_id));
        item.setAttribute('data-product-id', String(product.id));
        item.setAttribute('data-price', String(product.price));
        item.setAttribute('data-available', isUnavailable ? 'false' : 'true');

        const imageWrap = document.createElement('div');
        imageWrap.className = 'bk-media-bundle-builder__drawer-item-image';
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 56;
          img.height = 56;
          imageWrap.appendChild(img);
        }

        const info = document.createElement('div');
        info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';
        const title = document.createElement('span');
        title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
        title.textContent = formatDrawerProductTitle(product.title);
        info.appendChild(title);
        /* START: drawer product badge */
        if (product.badge) {
          const badge = document.createElement('span');
          badge.className = 'badge badge--primary bk-media-bundle-builder__drawer-item-badge';
          badge.textContent = product.badge;
          info.appendChild(badge);
        }
        /* END: drawer product badge */
        if (product.subtitle) {
          const subtitle = document.createElement('span');
          subtitle.className = 'text-subdued text-sm';
          subtitle.textContent = product.subtitle;
          info.appendChild(subtitle);
        }

        if (isUnavailable) {
          const soldOut = document.createElement('span');
          soldOut.className = 'text-xs bk-media-bundle-builder__colors-variant-sold-out';
          soldOut.textContent = 'Ausverkauft';
          info.appendChild(soldOut);
        }

        if (this.defaultUnitPrice > 0) {
          const delta = product.price - this.defaultUnitPrice;
          if (delta !== 0) {
            const deltaSpan = document.createElement('span');
            deltaSpan.className = 'bk-media-bundle-builder__drawer-price-delta text-subdued text-sm';
            deltaSpan.textContent = formatPriceDelta(delta);
            info.appendChild(deltaSpan);
          }
        }

        item.appendChild(imageWrap);
        item.appendChild(info);
        if (qtyTemplate) {
          item.appendChild(qtyTemplate.content.cloneNode(true));
        }

        if (this.limitOnePerProduct && this.limitOnePerProductNote) {
          const note = document.createElement('p');
          note.className = 'bk-media-bundle-builder__drawer-unique-note text-sm';
          note.setAttribute('data-unique-limit-note', '');
          note.setAttribute('role', 'status');
          note.hidden = true;
          note.textContent = this.limitOnePerProductNote;
          item.appendChild(note);
        }

        const plusBtn = item.querySelector('[data-qty-plus]');
        if (plusBtn && isUnavailable) plusBtn.disabled = true;

        fragment.appendChild(item);
      });

      this.drawerList.innerHTML = '';
      this.drawerList.appendChild(fragment);
    }

    _ensureDrawerUpgradeProgressRefs() {
      if (!this.drawer) return;
      if (this.drawerUpgradeProgress) return;
      this.drawerUpgradeProgress = this.drawer.querySelector('[data-drawer-upgrade-progress]');
      this.drawerUpgradeProgressText = this.drawer.querySelector(
        '[data-drawer-upgrade-progress-text]'
      );
      this.drawerUpgradeProgressBadge = this.drawer.querySelector(
        '[data-drawer-upgrade-progress-badge]'
      );
    }

    _syncDrawerQuantitiesFromSelections() {
      this.selections = this._sanitizeSelections(this.selections);
      const maxQty = this._getMaxSelectableQty();
      if (this.selections.length > maxQty) {
        this.selections = this.selections.slice(0, maxQty);
      }
      this.drawerQuantities = {};
      this.selections.forEach((sel) => {
        const id = String(sel.variantId);
        this.drawerQuantities[id] = (this.drawerQuantities[id] || 0) + 1;
      });
      this.drawerQuantities = this._capQuantitiesToLimit(this.drawerQuantities, maxQty);
      this._drawerLastTotal = this._getDrawerTotalQty();
    }

    _openDrawer() {
      this._ensureDrawerProductsRendered();
      this._syncDrawerQuantitiesFromSelections();
      this._hideUniqueLimitNotes();
      this._clearCelebration(this._getRecipeCelebrationContext());
      this._syncDrawerQtyDisplay();
      this._updateDrawerPricing();
      if (this.flexibleSelection) {
        this._ensureDrawerUpgradeProgressRefs();
        this._updateFlexibleProgressBanner();
      } else if (this.optionalExtraSlot) {
        this._ensureDrawerUpgradeProgressRefs();
        this._updateOptionalProgressBanner();
      }
      this.drawer?.show?.();
    }

    _ensureDrawerProductsRendered() {
      if (this._drawerProductsRendered) return;
      this._renderDrawerProducts();
      this._drawerProductsRendered = true;
    }

    _getDrawerTotalQty() {
      return Object.values(this.drawerQuantities).reduce((a, b) => a + b, 0);
    }

    _getSelectedKitRow() {
      return this.querySelector('[data-bundle-kit-row].is-selected');
    }

    _getRequiredQty() {
      if (this.flexibleSelection) {
        return this._getBaseRequiredQty();
      }
      const rowQty = parseInt(this._getSelectedKitRow()?.getAttribute('data-preset-quantity'), 10);
      if (rowQty) return rowQty;
      return parseInt(this.activePreset?.quantity, 10) || 1;
    }

    _getGiftItems() {
      const items = this.giftPricing?.items || [];
      if (this.giftMode === 'single_always') {
        return items.filter((item) => item.slot === 1);
      }
      return items;
    }

    _shouldRequireGiftSelection() {
      if (this.giftOptional && !this.gift1OptIn) return false;
      return this._getApplicableGiftItems().length > 0;
    }

    _getGiftItem(slot) {
      return this._getGiftItems().find((item) => item.slot === slot);
    }

    _getVariantCompareCents(variant) {
      if (!variant) return 0;
      const compare = parseInt(variant.compare_at_price, 10) || 0;
      const price = parseInt(variant.price, 10) || 0;
      return compare > price ? compare : price;
    }

    _findGiftVariant(item, selections) {
      if (!item?.variants?.length) return null;
      if (!item.options?.length) {
        return item.variants.find((variant) => variant.available) || item.variants[0];
      }
      return item.variants.find((variant) =>
        item.options.every((option) => {
          const position = option.position || item.options.indexOf(option) + 1;
          const key = `option${position}`;
          return variant[key] === selections[key];
        })
      );
    }

    _getSelectedGiftVariant(slot) {
      const item = this._getGiftItem(slot);
      if (!item) return null;
      const variantId = this.giftSelections[slot];
      if (!variantId) return null;
      return item.variants.find((variant) => variant.id === variantId) || null;
    }

    _getGiftCompareAdd() {
      return this._getApplicableGiftItems().reduce((sum, item) => {
        const selected = this._getSelectedGiftVariant(item.slot);
        if (selected) return sum + this._getVariantCompareCents(selected);
        if (this._isGiftOptIn(item) && this._isGiftOptInDeclined(item.slot)) return sum;
        const fallback = item.variants?.[0];
        return sum + this._getVariantCompareCents(fallback);
      }, 0);
    }

    _initGiftSelections() {
      this.giftSelections = {};
      this.giftOptInDeclined = {};
      this._getGiftItems().forEach((item) => {
        if (item.separateCart) {
          const variant = (item.variants || []).find((entry) => entry.available);
          if (variant) {
            this.giftSelections[item.slot] = variant.id;
            this._updateGiftCardUi(item.slot, variant, item);
          }
          return;
        }
        if (this._isGiftOptIn(item)) {
          this.giftOptInDeclined[item.slot] = true;
          delete this.giftSelections[item.slot];
          this._updateGiftCardUi(item.slot, null, item);
          return;
        }
        if (item.variants?.length === 1) {
          const variant = item.variants[0];
          if (variant?.available) {
            this.giftSelections[item.slot] = variant.id;
            this._updateGiftCardUi(item.slot, variant, item);
          }
        } else {
          this._updateGiftCardUi(item.slot, null, item);
        }
      });
      this._updateColorsSlotUi();
      this._updateUi();
    }

    _updateGiftCardUi(slot, variant, item) {
      const card = this.querySelector(`[data-gift-card][data-gift-slot="${slot}"]`);
      if (!card) return;
      const giftItem = item || this._getGiftItem(slot);
      const label = card.querySelector(`[data-gift-variant-label][data-gift-slot="${slot}"]`);
      const image = card.querySelector('[data-gift-image]');
      const pickLink = card.querySelector('.bk-media-bundle-builder__gift-color-link');

      if (label) {
        if (giftItem?.variants?.length === 1) {
          const displayLabel = this._getGiftVariantDisplayLabel(giftItem.variants[0]);
          label.textContent = displayLabel;
          label.hidden = !displayLabel;
        } else if (variant) {
          const displayLabel = this._getGiftVariantDisplayLabel(variant);
          label.textContent = displayLabel;
          label.hidden = !displayLabel;
          label.classList.remove('is-missing');
        } else {
          label.textContent = '';
          label.hidden = true;
          label.classList.add('is-missing');
        }
      }

      if (image) {
        const nextImage = this._getGiftVariantImage(variant, giftItem);
        if (nextImage) {
          image.src = nextImage;
          image.removeAttribute('srcset');
        }
      }

      if (pickLink) {
        pickLink.classList.toggle('is-selected', Boolean(variant));
      }
    }

    _getGiftVariantImage(variant, item) {
      if (variant?.image) return variant.image;
      return item?.fallbackImage || null;
    }

    _getGiftVariantDisplayLabel(variant) {
      const title = (variant?.title || '').trim();
      if (!title || title === 'Default Title') return '';
      return title
        .replace(/^Eiswürfelform\s*[-–]\s*/i, '')
        .replace(/^Eis-Silikonform\s*[-–]\s*/i, '');
    }

    _getGiftOptionPosition(option, optionIndex) {
      return option.position || optionIndex + 1;
    }

    _findGiftVariantByOptionValue(item, optionPosition, value) {
      return item.variants?.find((variant) => variant[`option${optionPosition}`] === value) || null;
    }

    _getAvailableGiftVariants(item) {
      return (item?.variants || []).filter((variant) => variant.available);
    }

    _shouldShowSoldOutGiftVariants(item) {
      if (this.drawerShowAllSoldOut) return true;
      const variants = item?.variants || [];
      return variants.length > 0 && this._getAvailableGiftVariants(item).length === 0;
    }

    _getVisibleGiftVariants(item) {
      if (this._shouldShowSoldOutGiftVariants(item)) return item?.variants || [];
      return this._getAvailableGiftVariants(item);
    }

    _isGiftOptionValueAvailable(item, optionPosition, value) {
      return this._getAvailableGiftVariants(item).some(
        (variant) => variant[`option${optionPosition}`] === value
      );
    }

    _shouldShowGiftOptionValue(item, optionPosition, value) {
      if (this._shouldShowSoldOutGiftVariants(item)) return true;
      return this._isGiftOptionValueAvailable(item, optionPosition, value);
    }

    _getInitialGiftPickerSelections(item, selectedVariant) {
      const initialSelections = {};
      if (selectedVariant) {
        item.options.forEach((option, optionIndex) => {
          const position = this._getGiftOptionPosition(option, optionIndex);
          initialSelections[`option${position}`] = selectedVariant[`option${position}`];
        });
        return initialSelections;
      }
      const firstAvailable = item.variants?.find((variant) => variant.available) || item.variants?.[0];
      item.options.forEach((option, optionIndex) => {
        const position = this._getGiftOptionPosition(option, optionIndex);
        initialSelections[`option${position}`] = firstAvailable?.[`option${position}`];
      });
      return initialSelections;
    }

    _createGiftPickerImage(src, alt, className) {
      if (!src) return null;
      const image = document.createElement('img');
      image.src = src;
      image.alt = alt || '';
      image.loading = 'lazy';
      image.width = 100;
      image.height = 100;
      image.className = className;
      return image;
    }

    _createGiftPickerVariantTile(slot, item, variant, optionPosition, checked) {
      const value = variant[`option${optionPosition}`];
      const label = document.createElement('label');
      label.className = 'bk-media-gift-picker__variant';
      if (!variant.available) label.classList.add('is-unavailable');
      if (checked) label.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `gift-${slot}-option-${optionPosition}`;
      input.value = value;
      input.checked = checked;
      input.disabled = !variant.available;
      input.addEventListener('change', () => this._syncActivePickerState(slot));
      label.addEventListener('click', () => {
        requestAnimationFrame(() => this._syncActivePickerState(slot));
      });

      const image = this._createGiftPickerImage(
        this._getGiftVariantImage(variant, item),
        value,
        'bk-media-gift-picker__variant-image'
      );

      const text = document.createElement('span');
      text.className = 'text-xs';
      text.textContent = value;

      label.appendChild(input);
      if (image) label.appendChild(image);
      label.appendChild(text);
      return label;
    }

    _createGiftPickerValueOption(slot, item, optionPosition, value, checked) {
      const variant = this._findGiftVariantByOptionValue(item, optionPosition, value);
      const label = document.createElement('label');
      label.className = 'bk-media-gift-picker__value h-stack gap-2 align-center';
      if (variant && !variant.available) label.classList.add('is-unavailable');
      if (checked) label.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `gift-${slot}-option-${optionPosition}`;
      input.value = value;
      input.checked = checked;
      input.disabled = variant ? !variant.available : false;
      input.addEventListener('change', () => this._syncActivePickerState(slot));
      label.addEventListener('click', () => {
        requestAnimationFrame(() => this._syncActivePickerState(slot));
      });

      const image = this._createGiftPickerImage(
        this._getGiftVariantImage(variant, item),
        value,
        'bk-media-gift-picker__value-image'
      );

      const text = document.createElement('span');
      text.className = 'text-sm';
      text.textContent = value;

      label.appendChild(input);
      if (image) label.appendChild(image);
      label.appendChild(text);
      return label;
    }

    _renderGiftPickerOptions(slot, item, initialSelections, wrapper) {
      item.options.forEach((option, optionIndex) => {
        const position = this._getGiftOptionPosition(option, optionIndex);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'bk-media-gift-picker__option v-stack gap-2';
        fieldset.setAttribute('data-gift-option-position', String(position));

        const legend = document.createElement('legend');
        legend.className = 'text-sm bold';
        legend.textContent = option.name;
        fieldset.appendChild(legend);

        const valuesWrap = document.createElement('div');
        const useVariantGrid = item.options.length === 1 && item.variants?.length > 1;
        valuesWrap.className = useVariantGrid
          ? 'bk-media-gift-picker__variants'
          : 'bk-media-gift-picker__values v-stack gap-2';

        if (useVariantGrid) {
          this._getVisibleGiftVariants(item).forEach((variant) => {
            const value = variant[`option${position}`];
            valuesWrap.appendChild(
              this._createGiftPickerVariantTile(
                slot,
                item,
                variant,
                position,
                initialSelections[`option${position}`] === value
              )
            );
          });
        } else {
          (option.values || []).forEach((value) => {
            if (!this._shouldShowGiftOptionValue(item, position, value)) return;
            valuesWrap.appendChild(
              this._createGiftPickerValueOption(
                slot,
                item,
                position,
                value,
                initialSelections[`option${position}`] === value
              )
            );
          });
        }

        fieldset.appendChild(valuesWrap);
        wrapper.appendChild(fieldset);
      });
    }

    _getGiftPickerSelections(slot, item, root) {
      const container = root || this.giftDrawerContent;
      const selections = {};
      item.options.forEach((option, optionIndex) => {
        const position = option.position || optionIndex + 1;
        const checked = container?.querySelector(
          `input[name="gift-${slot}-option-${position}"]:checked`
        );
        if (checked) selections[`option${position}`] = checked.value;
      });
      return selections;
    }

    _syncActivePickerState(slot) {
      if (this._activePickerContext === 'colors') {
        this._syncColorsDrawerState();
        return;
      }
      this._syncGiftPickerState(slot);
    }

    _syncGiftPickerState(slot) {
      const item = this._getGiftItem(slot);
      if (!item || !this.giftDrawerContent) return;

      const selections = this._getGiftPickerSelections(slot, item, this.giftDrawerContent);
      const allSelected = item.options.every((option, optionIndex) => {
        const position = option.position || optionIndex + 1;
        return Boolean(selections[`option${position}`]);
      });
      const variant = allSelected ? this._findGiftVariant(item, selections) : null;
      const status = this.giftPickerStatus;
      const confirm = this.giftPickerConfirm;

      if (status) {
        if (!allSelected || (variant && variant.available)) {
          status.textContent = '';
        } else if (!variant) {
          status.textContent = 'Diese Kombination ist nicht verfügbar.';
        } else if (!variant.available) {
          status.textContent = 'Diese Variante ist ausverkauft.';
        }
      }

      if (confirm) {
        confirm.disabled = !variant || !variant.available;
      }

      this.giftDrawerContent?.querySelectorAll('.bk-media-gift-picker__variant, .bk-media-gift-picker__value').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        label.classList.toggle('is-selected', Boolean(input?.checked));
      });
    }

    _confirmGiftPicker(slot) {
      const item = this._getGiftItem(slot);
      if (!item) return;
      const selections = this._getGiftPickerSelections(slot, item);
      const variant = this._findGiftVariant(item, selections);
      if (!variant || !variant.available) return;

      this.giftSelections[slot] = variant.id;
      this._updateGiftCardUi(slot, variant, item);
      this._closeGiftModal();
      this._updateColorsSlotUi();
      this._updateUi();
      this._updateDrawerPricing();
    }

    _allGiftsSelected() {
      return this._getApplicableGiftItems().every((item) => {
        if (item.separateCart) {
          const available = (item.variants || []).some((entry) => entry.available);
          if (!available) return true;
          return Boolean(this.giftSelections[item.slot]);
        }
        if (this._isGiftOptIn(item)) {
          return this._isGiftOptInDeclined(item.slot) || Boolean(this.giftSelections[item.slot]);
        }
        if (item.variants?.length === 1) return Boolean(item.variants[0]?.available);
        return Boolean(this.giftSelections[item.slot]);
      });
    }

    _getProductKey(product, variantId) {
      return product?.id || variantId;
    }

    _isUniqueProductLimitReached(product, variantId) {
      if (!this.limitOnePerProduct) return false;
      const id = String(variantId);
      if ((this.drawerQuantities[id] || 0) >= 1) return true;
      const productKey = this._getProductKey(product, id);
      return this.products.some((entry) => {
        const entryId = String(entry.variant_id);
        if (entryId === id) return false;
        if (this._getProductKey(entry, entryId) !== productKey) return false;
        return (this.drawerQuantities[entryId] || 0) >= 1;
      });
    }

    _hideUniqueLimitNotes() {
      if (this._uniqueLimitNoteTimeout) {
        clearTimeout(this._uniqueLimitNoteTimeout);
        this._uniqueLimitNoteTimeout = null;
      }
      this.drawerList?.querySelectorAll('[data-drawer-item]').forEach((item) => {
        item.classList.remove('is-unique-limit');
        const note = item.querySelector('[data-unique-limit-note]');
        if (note) note.hidden = true;
      });
    }

    _showUniqueLimitNote(variantId) {
      if (!this.limitOnePerProduct) return;
      this._hideUniqueLimitNotes();
      const item = this.drawerList?.querySelector(
        `[data-drawer-item][data-variant-id="${variantId}"]`
      );
      const note = item?.querySelector('[data-unique-limit-note]');
      if (!item || !note) return;
      item.classList.add('is-unique-limit');
      note.hidden = false;
      note.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      this._uniqueLimitNoteTimeout = setTimeout(() => {
        this._hideUniqueLimitNotes();
      }, 5000);
    }

    _changeDrawerQty(variantId, delta) {
      const required = this._getRequiredQty();
      const maxQty = this._getMaxSelectableQty();
      const current = this._getDrawerTotalQty();
      const id = String(variantId);
      const product = this.products.find((entry) => String(entry.variant_id) === id);
      if (delta > 0 && !this._isDrawerProductAvailable(product)) return;
      if (delta > 0 && this._isUniqueProductLimitReached(product, id)) {
        this._showUniqueLimitNote(id);
        return;
      }
      const next = (this.drawerQuantities[id] || 0) + delta;
      if (delta > 0 && current >= maxQty) return;
      if (next < 0) return;
      this._hideUniqueLimitNotes();
      if (next === 0) {
        delete this.drawerQuantities[id];
      } else {
        this.drawerQuantities[id] = next;
      }
      if (this.useKitIndexAsQuantity || this.optionalExtraSlot) {
        this._checkOptionalKitSync();
      }
      this._syncDrawerQtyDisplay();
      this._updateDrawerPricing();
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      if (this.flexibleSelection) {
        this._checkFlexibleUpgrade();
        this._checkFlexibleDowngrade();
        this._updateFlexibleProgressBanner();
      } else if (this.optionalExtraSlot) {
        this._updateOptionalProgressBanner();
      }
    }

    _syncDrawerQtyDisplay() {
      const required = this._getRequiredQty();
      const total = this._getEffectiveSelectionCount();
      const previousTotal = this._drawerLastTotal ?? 0;
      const maxQty = this._getMaxSelectableQty();
      const isComplete = this._hasOptionalExtraSlots() ? total >= required : total === required;
      const celebrationCtx = this._getRecipeCelebrationContext();

      if (this._shouldTriggerCurrentGoalCelebration(total, previousTotal, required)) {
        celebrationCtx.message = this._getGoalCelebrationMessage(total, required);
        this._triggerCelebration(celebrationCtx);
      } else if (this._shouldClearGoalCelebration(total, required)) {
        this._clearCelebration(celebrationCtx);
      }

      this._drawerLastTotal = total;

      this.drawerList?.querySelectorAll('[data-drawer-item]').forEach((item) => {
        const id = item.getAttribute('data-variant-id');
        const val = this.drawerQuantities[id] || 0;
        const input = item.querySelector('[data-qty-value]');
        if (input) {
          input.value = String(val);
          input.size = Math.max(String(val).length + 1, 2);
        }
        const minusBtn = item.querySelector('[data-qty-minus]');
        const plusBtn = item.querySelector('[data-qty-plus]');
        const isUnavailable = item.getAttribute('data-available') === 'false';
        if (minusBtn) minusBtn.disabled = val <= 0;
        if (plusBtn) plusBtn.disabled = isUnavailable || total >= maxQty;
        item.classList.toggle('is-selected', val > 0);
      });
      if (this.drawerConfirmTitle && this.drawerConfirmCount) {
        if (isComplete) {
          this.drawerConfirmTitle.hidden = true;
          this.drawerConfirmCount.hidden = false;
          this.drawerConfirmCount.textContent = `🎉 Auswählen (${total})`;
          this.drawerConfirmCount.classList.add('bold');
        } else {
          this.drawerConfirmTitle.hidden = false;
          this.drawerConfirmCount.hidden = false;
          this.drawerConfirmTitle.textContent = this._drawerConfirmDefaultTitle;
          this.drawerConfirmTitle.classList.add('bold');
          this.drawerConfirmCount.classList.remove('bold');
          this.drawerConfirmCount.textContent = `${total}/${required} ausgewählt`;
        }
      }
      if (this.drawerConfirm) {
        this.drawerConfirm.disabled = !isComplete;
      }

      if (this.flexibleSelection) {
        this._updateFlexibleProgressBanner();
      } else if (this.optionalExtraSlot) {
        this._updateOptionalProgressBanner();
      }
    }

    _positionDrawerCelebration(celebration, confirm) {
      if (!celebration || !confirm) return;
      const footer = celebration.parentElement;
      if (!footer) return;
      const footerRect = footer.getBoundingClientRect();
      const btnRect = confirm.getBoundingClientRect();
      const x = btnRect.left + btnRect.width / 2 - footerRect.left;
      const y = btnRect.top + btnRect.height / 2 - footerRect.top;
      celebration.style.left = `${x}px`;
      celebration.style.top = `${y}px`;
    }

    _spawnConfettiBurst(celebration, drawer) {
      if (!celebration) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return;

      const accent =
        getComputedStyle(drawer || this).getPropertyValue('--bb-accent').trim() || '#E8614D';
      const colors = [accent, '#2d7a2d', '#F5A623', '#FFD54F', '#9B59B6', '#81D4FA', '#E91E8C'];
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < 64; i++) {
        const angle = Math.random() * Math.PI * 2;
        const velocity = 55 + Math.random() * 95;
        const burstX = Math.round(Math.cos(angle) * velocity);
        const burstY = Math.round(Math.sin(angle) * velocity - 25);
        const size = 6 + Math.random() * 6;
        const piece = document.createElement('span');
        piece.className = 'bk-media-bundle-builder__drawer-confetti-piece';
        piece.style.setProperty('--burst-x', `${burstX}px`);
        piece.style.setProperty('--burst-y', `${burstY}px`);
        piece.style.setProperty('--burst-rotate', `${Math.round(Math.random() * 540 - 270)}deg`);
        piece.style.setProperty('--burst-delay', `${Math.random() * 0.08}s`);
        piece.style.width = `${size}px`;
        piece.style.height = `${size}px`;
        piece.style.background = colors[Math.floor(Math.random() * colors.length)];
        piece.style.borderRadius = Math.random() > 0.45 ? '50%' : '2px';
        fragment.appendChild(piece);
      }

      celebration.appendChild(fragment);
    }

    _triggerCelebration(ctx) {
      const { celebration, confirm, drawer, timeoutKey, goalToast, message } = ctx;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isBestpriceGoal = this._isBestpriceGoalMessage(message);

      if (goalToast && message) {
        goalToast.textContent = message;
        goalToast.hidden = false;
        goalToast.classList.add('is-visible');
        goalToast.classList.toggle('is-bestprice-goal', isBestpriceGoal);
      }

      if (celebration) {
        celebration.innerHTML = '';
        celebration.setAttribute('aria-hidden', 'false');
        celebration.classList.add('is-active');
        this._positionDrawerCelebration(celebration, confirm);
        if (!reducedMotion) {
          requestAnimationFrame(() => {
            this._positionDrawerCelebration(celebration, confirm);
            this._spawnConfettiBurst(celebration, drawer);
          });
        }
      }

      clearTimeout(this[timeoutKey]);
      this[timeoutKey] = setTimeout(() => {
        if (celebration) {
          celebration.classList.remove('is-active');
          celebration.setAttribute('aria-hidden', 'true');
          celebration.innerHTML = '';
        }
      }, 1500);

      clearTimeout(this._goalToastTimeout);
      const toastDuration = isBestpriceGoal ? this._getBestpriceGoalToastDuration() : 1500;
      this._goalToastTimeout = setTimeout(() => {
        if (goalToast) {
          goalToast.classList.remove('is-visible', 'is-bestprice-goal');
          goalToast.hidden = true;
          goalToast.textContent = '';
        }
      }, toastDuration);
    }

    _clearCelebration(ctx) {
      const { celebration, timeoutKey, goalToast } = ctx;

      clearTimeout(this[timeoutKey]);
      this[timeoutKey] = null;
      clearTimeout(this._goalToastTimeout);
      this._goalToastTimeout = null;

      if (goalToast) {
        goalToast.classList.remove('is-visible', 'is-bestprice-goal');
        goalToast.hidden = true;
        goalToast.textContent = '';
      }

      if (celebration) {
        celebration.classList.remove('is-active');
        celebration.setAttribute('aria-hidden', 'true');
        celebration.innerHTML = '';
      }
    }

    _getPresetForRow(row) {
      if (!row) return null;
      const kitValue = row.getAttribute('data-kit-value');
      const presetIndex = parseInt(row.getAttribute('data-preset-index'), 10);
      return (
        this.presets.find((preset) => preset.kitValue === kitValue) ||
        (presetIndex ? this.presets.find((preset) => preset.presetIndex === presetIndex) : null)
      );
    }

    _getKitVariantForRow(row) {
      const variantId = row?.getAttribute('data-variant-id');
      if (!variantId) return null;
      return this.variants.find((variant) => String(variant.id) === variantId) || null;
    }

    _setKitRowVariantAttributes(row, variant, fallbackPrice) {
      if (!row || !variant) return;
      row.setAttribute('data-variant-id', String(variant.id));
      const price = parseInt(variant.price, 10) || 0;
      row.setAttribute('data-variant-price', String(price));
      const compare = parseInt(variant.compare_at_price, 10) || 0;
      row.setAttribute(
        'data-variant-compare-at',
        String(compare > price ? compare : price)
      );
    }

    _getKitVariantCents(row) {
      const fromAttr = parseInt(row?.getAttribute('data-variant-price'), 10);
      if (!Number.isNaN(fromAttr) && fromAttr >= 0) {
        return fromAttr;
      }
      const variant = this._getKitVariantForRow(row);
      if (variant && Number.isFinite(parseInt(variant.price, 10))) {
        return parseInt(variant.price, 10) || 0;
      }
      return 0;
    }

    _getKitVariantCompareCents(row) {
      const price = this._getKitVariantCents(row);
      const fromAttr = parseInt(row?.getAttribute('data-variant-compare-at'), 10);
      if (!Number.isNaN(fromAttr) && fromAttr > price) {
        return fromAttr;
      }
      if (this.variantPricingOnly || this.useKitIndexAsQuantity) {
        const preset = this._getPresetForRow(row);
        if (this.defaultUnitPrice > 0 && preset?.quantity) {
          return Math.max(price, this.defaultUnitPrice * preset.quantity);
        }
      }
      const variant = this._getKitVariantForRow(row);
      if (!variant) return price;
      const compare = parseInt(variant.compare_at_price, 10) || 0;
      return compare > price ? compare : price;
    }

    _getRowComputedPriceCents(row) {
      const computed = parseInt(row?.getAttribute('data-computed-price'), 10);
      return !Number.isNaN(computed) && computed > 0 ? computed : 0;
    }

    _getProductCompareCents(product) {
      if (!product) return 0;
      const compare = parseInt(product.compare_at_price, 10) || 0;
      const price = parseInt(product.price, 10) || 0;
      return compare > price ? compare : price;
    }

    _sumCardAmountsFromQuantities(quantities, targetQty, mode) {
      const qty = targetQty || 0;
      if (!qty) return 0;

      let selectedTotal = 0;
      let totalCount = 0;

      Object.entries(quantities || {}).forEach(([variantId, count]) => {
        const product = this.products.find((p) => String(p.variant_id) === variantId);
        if (!product || !count) return;
        const amount =
          mode === 'compare' ? this._getProductCompareCents(product) : parseInt(product.price, 10) || 0;
        selectedTotal += amount * count;
        totalCount += count;
      });

      const remaining = Math.max(0, qty - totalCount);
      const defaultPerSlot = this.defaultUnitPrice;

      if (totalCount === 0) {
        return defaultPerSlot * qty;
      }

      return selectedTotal + defaultPerSlot * remaining;
    }

    _getGiftUnitCompare(item, variant) {
      const override = parseInt(item?.compareOverride, 10) || 0;
      const qty = Math.max(parseInt(item?.quantity, 10) || 1, 1);
      if (qty > 1 && override > 0) return override;
      return this._getVariantCompareCents(variant || item?.variants?.[0]);
    }

    _getGiftItemCompareCents(item, variant) {
      const qty = Math.max(parseInt(item?.quantity, 10) || 1, 1);
      return this._getGiftUnitCompare(item, variant) * qty;
    }

    _applyHalloweenCompare(pricing, presetIndex) {
      if (!pricing) return pricing;
      const index = parseInt(presetIndex, 10) || 1;
      let extra = 0;

      const separate = this._getGiftItems().find((item) => item.separateCart);
      const separatePreset = this.presets.find((entry) => entry.presetIndex === index);
      const separateCustomPrice = parseInt(separatePreset?.customPriceCents, 10) || 0;
      const separateCustomCompare = this.enableCustomPresetComparePrices
        ? separatePreset?.customCompareCents || 0
        : 0;
      const flatPresetPrice = separateCustomPrice > 0 && separateCustomCompare <= separateCustomPrice;
      const bundleGroupName = (this.bundleGroup || this.getAttribute('data-bundle-group') || '')
        .trim()
        .toLowerCase();
      if (separate && bundleGroupName !== 'superlimo' && !flatPresetPrice) {
        const selected = this._getSelectedGiftVariant(separate.slot);
        const qty = Math.max(parseInt(separate.quantity, 10) || 1, 1);
        extra += this._getGiftUnitCompare(separate, selected) * qty;
      }

      if (!extra) return pricing;
      const compareTotal = (pricing.compareTotal || 0) + extra;
      const finalTotal = pricing.finalTotal || 0;
      return {
        ...pricing,
        compareTotal,
        savings: Math.max(0, compareTotal - finalTotal)
      };
    }

    _getGiftCompareForQuantity(quantity, presetIndex) {
      const qty = quantity || 0;
      if (!qty) return 0;

      const pricingPresetIndex =
        (presetIndex ?? parseInt(this.activePreset?.presetIndex, 10)) || 1;
      if (this.flexibleSelection && pricingPresetIndex === 3) return 0;

      if (this.giftMode === 'single_always') {
        const gift1 = this._getGiftItem(1);
        if (!gift1) return 0;
        const selected = this._getSelectedGiftVariant(1);
        if (selected) return this._getVariantCompareCents(selected);
        if (this.giftOptional || (this._isGiftOptIn(gift1) && this._isGiftOptInDeclined(1))) return 0;
        return this._getVariantCompareCents(gift1.variants?.[0]);
      }

      const minQty = parseInt(this.giftPricing?.minQuantity, 10) || 4;
      const topQty = this._getTopPresetQuantity();
      let total = 0;

      if (qty >= minQty) {
        this._getGiftItems()
          .filter((item) => {
            if (item.separateCart) return false;
            if (item.slot === 3) return false;
            if (this._isGiftProfiKitOnly(item)) return false;
            if (this._isGiftStarterProfiOnly(item)) {
              return this._isStarterOrProfiPreset(pricingPresetIndex);
            }
            return true;
          })
          .forEach((item) => {
            if (this._isGiftOptIn(item) && pricingPresetIndex === 1) return;
            const selected = this._getSelectedGiftVariant(item.slot);
            if (selected) {
              total += this._getGiftItemCompareCents(item, selected);
            } else if (this._isGiftOptIn(item) && this._isGiftOptInDeclined(item.slot)) {
              return;
            } else {
              total += this._getGiftItemCompareCents(item, item.variants?.[0]);
            }
          });

        this._getGiftItems()
          .filter((item) => this._isGiftProfiKitOnly(item) && !item.separateCart)
          .forEach((item) => {
            if ((item.slot === 4 || item.slot === 5) && pricingPresetIndex !== 2) return;
            if (pricingPresetIndex === 1) return;
            const selected = this._getSelectedGiftVariant(item.slot);
            if (selected) {
              total += this._getGiftItemCompareCents(item, selected);
            } else {
              total += this._getGiftItemCompareCents(item, item.variants?.[0]);
            }
          });
      }

      if (qty >= topQty) {
        const gift3 = this._getGiftItem(3);
        if (gift3) {
          const selected = this._getSelectedGiftVariant(3);
          if (selected) {
            total += this._getVariantCompareCents(selected);
          } else {
            total += this._getVariantCompareCents(gift3.variants?.[0]);
          }
        }
      }

      return total;
    }

    _applyCustomPresetCompareToPricing(pricing, presetIndex) {
      if (!pricing) return pricing;

      const preset = this.presets.find((entry) => entry.presetIndex === presetIndex);
      const customPrice = parseInt(preset?.customPriceCents, 10) || 0;
      const customCompare = this.enableCustomPresetComparePrices
        ? preset?.customCompareCents || 0
        : 0;

      let finalTotal = pricing.finalTotal;
      if (customPrice > 0) {
        finalTotal = customPrice;
      } else if (this.enableCustomPresetComparePrices && customCompare > 0) {
        const discount = this._parseDiscountPercent(preset?.discountPercent || 0);
        if (discount > 0) {
          const discountBps = Math.round(discount * 100);
          finalTotal = Math.round((customCompare * (10000 - discountBps)) / 10000);
        }
      }

      let compareTotal = pricing.compareTotal;
      if (customPrice > 0 && customCompare <= finalTotal) {
        compareTotal = finalTotal;
      } else if (customCompare > finalTotal) {
        compareTotal = customCompare;
      }

      if (customPrice <= 0 && customCompare <= 0) {
        return this._applyHalloweenCompare(pricing, presetIndex);
      }
      if (customCompare > 0 && customCompare <= finalTotal && customPrice <= 0) {
        return this._applyHalloweenCompare(pricing, presetIndex);
      }

      return this._applyHalloweenCompare(
        {
          ...pricing,
          finalTotal,
          compareTotal,
          savings: Math.max(0, compareTotal - finalTotal)
        },
        presetIndex
      );
    }

    _finalizePresetPricing(result, qty, discountPercent, presetIndex) {
      return this._applyCustomPresetCompareToPricing(result, presetIndex);
    }

    _getAuthoritativeCartPricing(row, preset) {
      const quantities = this._getQuantitiesForKitRow(row, preset);
      let pricingQty = preset?.quantity || 0;
      let discount = preset?.discountPercent || 0;

      if (this.flexibleSelection) {
        pricingQty = this._getPricingQuantity();
        discount = this._getActiveDiscountPercent();
      } else if (this.optionalExtraSlot && this.useKitIndexAsQuantity) {
        pricingQty = this._getPricingQuantity();
        discount = this._getActiveDiscountPercent();
      }

      const pricing = this._calcPresetPricing(
        pricingQty,
        discount,
        quantities,
        this._getKitVariantCents(row),
        this._getKitVariantCompareCents(row),
        preset?.presetIndex
      );

      return {
        finalTotal: pricing.finalTotal,
        compareTotal: pricing.compareTotal,
        savings: Math.max(0, pricing.compareTotal - pricing.finalTotal)
      };
    }

    _resolveAuthoritativeListCompare(cartPricing, preset) {
      const finalTotal = Math.round(cartPricing.finalTotal);
      if (finalTotal <= 0) {
        return { compare: 0, final: 0, customCompare: 0 };
      }

      let compareTotal = Math.round(cartPricing.compareTotal);
      let customCompare = 0;

      if (this.enableCustomPresetComparePrices && preset) {
        customCompare = preset.customCompareCents || 0;
        if (customCompare > finalTotal) {
          compareTotal = customCompare;
        }
      }

      const compare = compareTotal > finalTotal ? compareTotal : 0;
      return {
        compare,
        final: finalTotal,
        customCompare: customCompare > finalTotal ? customCompare : 0
      };
    }

    _calcPresetPricing(
      quantity,
      discountPercent,
      quantities,
      kitVariantCents,
      kitCompareCents,
      presetIndex
    ) {
      const qty = quantity || 0;
      const discount = discountPercent ?? this.activePreset?.discountPercent ?? 0;
      const qtyMap = quantities || this.drawerQuantities || {};
      const kitPrice = kitVariantCents || 0;
      const kitCompare = Math.max(kitCompareCents || 0, kitPrice);
      const pricingPresetIndex =
        (presetIndex ?? parseInt(this.activePreset?.presetIndex, 10)) || 1;

      if (this.variantPricingOnly) {
        const giftCompare = this._getGiftCompareForQuantity(qty, pricingPresetIndex);
        const finalTotal = kitPrice;
        let productCompare = kitCompare;
        if (this.defaultUnitPrice > 0 && qty > 0) {
          productCompare = Math.max(kitCompare, this.defaultUnitPrice * qty);
        }
        const compareTotal = productCompare + giftCompare;
        const savings = Math.max(0, compareTotal - finalTotal);
        return this._finalizePresetPricing(
          { finalTotal, compareTotal, savings, discountPercent: discount },
          qty,
          discount,
          pricingPresetIndex
        );
      }

      if (this.useKitIndexAsQuantity && this.defaultUnitPrice > 0 && qty > 0) {
        const pricedPreset = this.presets.find((entry) => entry.presetIndex === pricingPresetIndex);
        const freeQty = parseInt(pricedPreset?.freeQuantity, 10) || 0;
        if (freeQty > 0) {
          const payQty = Math.max(qty - freeQty, 0);
          const finalTotal = this.defaultUnitPrice * payQty;
          const compareTotal = this.defaultUnitPrice * qty;
          const savings = Math.max(0, compareTotal - finalTotal);
          return this._finalizePresetPricing(
            { finalTotal, compareTotal, savings, discountPercent: 0 },
            qty,
            0,
            pricingPresetIndex
          );
        }

        const cardsSubtotal = this.defaultUnitPrice * qty;
        const limoFinal = this._applyCardsOrderDiscount(this.defaultUnitPrice, qty, discount);
        let giftCompare = this._getCardsOnlyGiftCompare(qty);
        let giftFinal = 0;
        if (this.includeGiftsInBundle) {
          giftCompare = this._getGiftCompareForQuantity(qty, pricingPresetIndex);
          giftFinal = this._applyBundleDiscount(giftCompare, discount);
        }
        const finalTotal = limoFinal + giftFinal;
        const compareTotal = cardsSubtotal + giftCompare;
        const savings = Math.max(0, compareTotal - finalTotal);
        return this._finalizePresetPricing(
          { finalTotal, compareTotal, savings, discountPercent: discount },
          qty,
          discount,
          pricingPresetIndex
        );
      }

      if (
        this.flexibleSelection &&
        pricingPresetIndex === 3 &&
        discount > 0 &&
        this.defaultUnitPrice > 0 &&
        qty > 0
      ) {
        const cardPriceSum = this._sumCardAmountsFromQuantities(qtyMap, qty, 'price');
        const cardsCompareSum = this._sumCardAmountsFromQuantities(qtyMap, qty, 'compare');
        const estimatedCardSubtotal = this.defaultUnitPrice * qty;
        let finalTotal;
        let compareTotal;

        if (cardPriceSum > 0) {
          const cardsDiscounted = this._applyBundleDiscount(cardPriceSum, discount);
          finalTotal = kitPrice + cardsDiscounted;
          compareTotal = kitCompare + cardsCompareSum;
        } else {
          const bundleBase = kitPrice + estimatedCardSubtotal;
          finalTotal = this._applyBundleDiscount(bundleBase, discount);
          compareTotal = kitCompare + estimatedCardSubtotal;
        }

        const savings = Math.max(0, compareTotal - finalTotal);
        return this._finalizePresetPricing(
          { finalTotal, compareTotal, savings, discountPercent: discount },
          qty,
          discount,
          pricingPresetIndex
        );
      }

      if (this.defaultUnitPrice > 0 && qty > 0) {
        const baseSubtotal = this.defaultUnitPrice * qty;
        const cardPriceSum = this._sumCardAmountsFromQuantities(qtyMap, qty, 'price');
        const giftCompare = this._getGiftCompareForQuantity(qty, pricingPresetIndex);
        const giftsInBundleBase = this.includeGiftsInBundle ? giftCompare : 0;
        const bundleBase = baseSubtotal + kitPrice + giftsInBundleBase;
        const bundleDiscounted = this._applyBundleDiscount(bundleBase, discount);
        const finalTotal = bundleDiscounted + (cardPriceSum - baseSubtotal);
        const compareTotal = kitCompare + cardPriceSum + giftCompare;
        const savings = Math.max(0, compareTotal - finalTotal);
        return this._finalizePresetPricing(
          { finalTotal, compareTotal, savings, discountPercent: discount },
          qty,
          discount,
          pricingPresetIndex
        );
      }

      let subtotal = 0;
      let compareTotal = kitCompare;
      Object.entries(qtyMap).forEach(([variantId, count]) => {
        const product = this.products.find((p) => String(p.variant_id) === variantId);
        if (!product) return;
        subtotal += product.price * count;
        const compare =
          product.compare_at_price > product.price ? product.compare_at_price : product.price;
        compareTotal += compare * count;
      });
      const giftCompare = this._getGiftCompareForQuantity(qty, pricingPresetIndex);
      compareTotal += giftCompare;
      const bundleBase = subtotal + kitPrice;
      const finalTotal = this._applyBundleDiscount(bundleBase, discount);
      const savings = Math.max(0, compareTotal - finalTotal);
      return this._finalizePresetPricing(
        { finalTotal, compareTotal, savings, discountPercent: discount },
        qty,
        discount,
        pricingPresetIndex
      );
    }

    _selectionsToQuantities(selections) {
      const quantities = {};
      (selections || []).forEach((sel) => {
        const id = String(sel.variantId);
        quantities[id] = (quantities[id] || 0) + 1;
      });
      return quantities;
    }

    _capQuantitiesToLimit(quantities, limit) {
      if (!limit || limit < 1) return {};
      const capped = {};
      let remaining = limit;

      Object.entries(quantities || {}).forEach(([variantId, count]) => {
        if (remaining <= 0 || !count) return;
        const take = Math.min(count, remaining);
        capped[variantId] = take;
        remaining -= take;
      });

      return capped;
    }

    _getActiveKitSelectionPreview() {
      if (this.selections?.length) {
        return this.selections;
      }
      const activeKit = this.activePreset?.kitValue;
      if (!activeKit) return null;
      const saved = this.presetSelections[activeKit];
      return saved?.length ? saved : null;
    }

    _getQuantitiesForKitRow(row, preset) {
      const kitValue = row?.getAttribute('data-kit-value');
      const drawerOpen = this.drawer?.open;
      const kitLimit =
        parseInt(preset?.quantity, 10) || parseInt(row?.getAttribute('data-preset-quantity'), 10) || 0;

      if (this._hasOptionalExtraSlots() && row?.classList.contains('is-selected')) {
        if (drawerOpen) {
          return this.drawerQuantities || {};
        }
        if (this.selections?.length) {
          return this._selectionsToQuantities(this.selections);
        }
      }

      if (drawerOpen) {
        return this._capQuantitiesToLimit(this.drawerQuantities, kitLimit);
      }

      if (kitValue && this.presetSelections[kitValue]?.length) {
        return this._selectionsToQuantities(this.presetSelections[kitValue]);
      }

      const preview = this._getActiveKitSelectionPreview();
      if (preview?.length) {
        return this._capQuantitiesToLimit(this._selectionsToQuantities(preview), kitLimit);
      }

      return {};
    }

    _displayedSavingsPercent(compareTotal, finalTotal) {
      const compare = Math.round(compareTotal || 0);
      const final = Math.round(finalTotal || 0);
      if (compare <= final || compare <= 0) return 0;
      return Math.round(((compare - final) * 100) / compare);
    }

    _applyShopDisplayDiscount(pricing) {
      const percent = this.shopDisplayDiscount;
      if (!percent || !pricing) return pricing;
      const listFinal = Math.round(pricing.finalTotal || 0);
      if (listFinal <= 0) return pricing;
      const finalTotal = Math.round((listFinal * (100 - percent)) / 100);
      const compareTotal = Math.max(Math.round(pricing.compareTotal || 0), listFinal);
      return {
        ...pricing,
        finalTotal,
        compareTotal,
        savings: Math.max(0, compareTotal - finalTotal)
      };
    }

    _updateKitRowPricing() {
      if (!this.defaultUnitPrice && !this.variantPricingOnly) return;

      this.querySelectorAll('[data-bundle-kit-row]').forEach((row) => {
        if (row.hasAttribute('data-direct-offer')) return;
        const preset = this._getPresetForRow(row);
        if (!preset) return;

        const isSelected = row.classList.contains('is-selected');
        const hasCustomCompare = this.enableCustomPresetComparePrices && preset.customCompareCents > 0;
        let pricingQty = preset.quantity;
        let discount = preset.discountPercent;

        if (!hasCustomCompare && this.flexibleSelection && isSelected) {
          pricingQty = this._getPricingQuantity();
          discount = this._getActiveDiscountPercent();
        } else if (!hasCustomCompare && this.optionalExtraSlot && isSelected && this.useKitIndexAsQuantity) {
          pricingQty = this._getPricingQuantity();
          discount = this._getActiveDiscountPercent();
        }

        const quantities = this._getQuantitiesForKitRow(row, preset);
        let pricing;

        if (this.useKitIndexAsQuantity || this.variantPricingOnly) {
          pricing = this._calcPresetPricing(
            pricingQty,
            discount,
            quantities,
            this._getKitVariantCents(row),
            this._getKitVariantCompareCents(row),
            preset.presetIndex
          );
        } else if (hasCustomCompare) {
          pricing = this._applyCustomPresetCompareToPricing(
            {
              finalTotal: 0,
              compareTotal: preset.customCompareCents,
              savings: 0,
              discountPercent: preset.discountPercent
            },
            preset.presetIndex
          );
        } else {
          const computed = this._getRowComputedPriceCents(row);
          if (computed > 0 && !Object.keys(quantities || {}).length && !this.flexibleSelection) {
            const giftCompare = this._getGiftCompareForQuantity(preset.quantity, preset.presetIndex);
            const kitCompare = this._getKitVariantCompareCents(row);
            const cardsCompare = this.defaultUnitPrice * preset.quantity;
            const compareTotal = kitCompare + cardsCompare + giftCompare;
            pricing = this._applyCustomPresetCompareToPricing(
              {
                finalTotal: computed,
                compareTotal,
                savings: Math.max(0, compareTotal - computed)
              },
              preset.presetIndex
            );
          } else {
            pricing = this._calcPresetPricing(
              pricingQty,
              discount,
              quantities,
              this._getKitVariantCents(row),
              this._getKitVariantCompareCents(row),
              preset.presetIndex
            );
          }
        }

        const { finalTotal, compareTotal, savings } = this._applyShopDisplayDiscount(pricing);
        const showCompare = compareTotal > finalTotal;

        const saleEl = row.querySelector('.bk-media-bundle-builder__price-sale');
        if (saleEl) {
          saleEl.textContent = formatMoney(finalTotal);
        }

        const compareEl = row.querySelector('[data-bundle-kit-compare]');
        if (compareEl) {
          if (showCompare) {
            compareEl.textContent = formatMoney(compareTotal);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        const savingsBadge = row.querySelector('[data-bundle-kit-savings]');
        if (savingsBadge) {
          const savingsPercent = this._displayedSavingsPercent(compareTotal, finalTotal);
          if (this.shopDisplayDiscount > 0 && savingsPercent > 0) {
            savingsBadge.textContent = `-${savingsPercent}%`;
            savingsBadge.hidden = false;
          } else if (savings > 0) {
            savingsBadge.textContent = `Du sparst ${formatMoneyAmount(savings)}\u00a0€`;
            savingsBadge.hidden = false;
          } else {
            savingsBadge.hidden = true;
          }
        }

        this._applyShopUnitSubtitle(row);
      });
    }

    _applyShopUnitSubtitle(row) {
      const el = row.querySelector('[data-shop-unit-subtitle]');
      if (!el || !this.shopDisplayDiscount) return;
      const source = el.getAttribute('data-shop-unit-subtitle') || '';
      if (!/\d+,\d+\s*€/.test(source)) return;
      const percent = this.shopDisplayDiscount;
      const prefix = el.textContent.startsWith('Ab ') ? 'Ab ' : '';
      el.textContent =
        prefix +
        source.replace(/(\d+),(\d+)(\s*€)/, (_, euros, cents, currency) => {
          const value = parseInt(euros, 10) * 100 + parseInt(cents, 10);
          const next = Math.round((value * (100 - percent)) / 100);
          const nextCents = String(next % 100).padStart(2, '0');
          return `${Math.floor(next / 100)},${nextCents}${currency}`;
        });
    }

    _updateDrawerPricing() {
      const pricingQty = this._getPricingQuantity();
      const discount = this._getActiveDiscountPercent();
      const kitRow =
        this.optionalExtraSlot && this.useKitIndexAsQuantity
          ? this._getOptionalPricingKitRow(pricingQty)
          : this._getSelectedKitRow();
      const preset = this._getPresetForRow(kitRow);
      const pricing = this._calcPresetPricing(
        pricingQty,
        discount,
        this.drawerQuantities,
        this._getKitVariantCents(kitRow),
        this._getKitVariantCompareCents(kitRow),
        parseInt(preset?.presetIndex || this.activePreset?.presetIndex, 10)
      );
      const { finalTotal, compareTotal, savings } = this._applyShopDisplayDiscount(pricing);
      const showCompare = compareTotal > finalTotal;

      if (this.drawerTotal) {
        this.drawerTotal.textContent = formatMoney(finalTotal);
      }
      if (this.drawerCompare) {
        if (showCompare) {
          this.drawerCompare.textContent = formatMoney(compareTotal);
          this.drawerCompare.hidden = false;
        } else {
          this.drawerCompare.hidden = true;
        }
      }
      if (this.drawerDiscountBadge) {
        const savingsPercent = this._displayedSavingsPercent(compareTotal, finalTotal);
        if (this.shopDisplayDiscount > 0 && savingsPercent > 0) {
          this.drawerDiscountBadge.textContent = `-${savingsPercent}%`;
          this.drawerDiscountBadge.hidden = false;
        } else if (showCompare && savings > 0) {
          this.drawerDiscountBadge.textContent = `-${formatMoney(savings)}`;
          this.drawerDiscountBadge.hidden = false;
        } else {
          this.drawerDiscountBadge.hidden = true;
        }
      }
    }

    _confirmDrawer() {
      const required = this._getRequiredQty();
      const total = this._getDrawerTotalQty();
      if (this._hasOptionalExtraSlots() ? total < required : total !== required) return;
      this.drawer?.hide?.();
    }

    _isComplete() {
      if (this._isDirectOfferSelected()) return true;
      const required = this._getRequiredQty();
      const selections = this._sanitizeSelections(this.selections);
      if (selections.length !== this.selections.length) {
        this.selections = selections;
        this._renderSlots();
      }
      if (this._hasOptionalExtraSlots()) {
        if (selections.length < required) return false;
      } else if (selections.length !== required) {
        return false;
      }
      if (this._shouldRequireGiftSelection() && !this._allGiftsSelected()) return false;
      return true;
    }

    _clearMissingHighlights() {
      this.querySelectorAll('.is-missing-highlight').forEach((el) => {
        el.classList.remove('is-missing-highlight');
      });
      if (this._missingHighlightTimeout) {
        clearTimeout(this._missingHighlightTimeout);
        this._missingHighlightTimeout = null;
      }
    }

    _highlightMissing(element) {
      this._clearMissingHighlights();
      if (!element) return;
      element.classList.add('is-missing-highlight');
      this._missingHighlightTimeout = setTimeout(() => {
        element.classList.remove('is-missing-highlight');
        this._missingHighlightTimeout = null;
      }, 4000);
    }

    _scrollToMissing(element) {
      if (!element) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      element.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center'
      });
    }

    _getFirstMissingTarget() {
      const required = this._getRequiredQty();

      if (this.selections.length < required) {
        const slots = this.querySelectorAll(
          '[data-bundle-cards-panel] [data-bundle-open-drawer]'
        );
        const missingIndex = this.selections.length;
        if (slots[missingIndex]) return slots[missingIndex];
        return this.querySelector('[data-bundle-cards-panel]');
      }

      if (this._shouldRequireGiftSelection()) {
        const items = this._getApplicableGiftItems().slice().sort((a, b) => a.slot - b.slot);
        for (const item of items) {
          if (item.variants?.length <= 1) continue;
          if (this.giftSelections[item.slot]) continue;
          if (item.slot === 3 || (item.slot === 2 && this._isGiftProfiKitOnly(item))) {
            const giftSlot = this.querySelector(`[data-colors-slot][data-gift-slot="${item.slot}"]`);
            if (giftSlot) return giftSlot;
            if (item.slot === 2) {
              const kitGiftTrigger = this.querySelector(
                '[data-bundle-kit-gift-trigger][data-gift-slot="2"]'
              );
              if (kitGiftTrigger) return kitGiftTrigger;
            }
            continue;
          }
          const slot = this.querySelector(`[data-colors-slot][data-gift-slot="${item.slot}"]`);
          if (slot) return slot;
          const kitTrigger = this.querySelector(
            `[data-bundle-kit-row].is-selected [data-bundle-kit-gift-trigger][data-gift-slot="${item.slot}"]`
          );
          if (kitTrigger) return kitTrigger;
        }
      }

      return null;
    }

    _showMissingSelectionHint() {
      const target = this._getFirstMissingTarget();
      if (!target) return;
      this._scrollToMissing(target);
      this._highlightMissing(target);
    }

    _updateGiftsVisibility() {
      if (!this._shouldShowBaseGifts() && this.giftDrawer?.open) {
        this._closeGiftModal();
      }
    }

    _updateUi() {
      this._clearInactiveGiftSelections();
      this._syncApplicableSingleVariantGifts();
      this._updateGiftsVisibility();
      this._updateColorsVisibility();
      this._updateColorsSlotUi();
      this._updateKitRowSwitchability();
      if (this.flexibleSelection) {
        this._updateFlexibleProgressBanner();
      }
      if (this._isComplete()) {
        this._clearMissingHighlights();
      }
      this._syncDirectOfferUi();
    }

    _shouldAddGiftInsideBundle(item, addonLineProps) {
      if (!addonLineProps || !item) return false;
      if (this.includeGiftsInBundle) return true;
      const goodieGroup = this._getBundleGroupWithGoodie();
      const bundleGroup = addonLineProps._BundleGroup || '';
      return (
        Boolean(goodieGroup) &&
        String(bundleGroup) === String(goodieGroup) &&
        this._isGiftOptIn(item)
      );
    }

    _appendGiftCartItems(items, instanceId, addonLineProps) {
      this._getApplicableGiftItems().forEach((item) => {
        let variantId = this.giftSelections[item.slot];
        if (!variantId && (item.variants?.length || 0) === 1 && item.variants[0]?.available) {
          variantId = item.variants[0].id;
          this.giftSelections[item.slot] = variantId;
        }
        const id = parseInt(variantId, 10);
        if (!id) return;
        const quantity = Math.max(parseInt(item.quantity, 10) || 1, 1);
        if (item.separateCart) {
          const discountId = addonLineProps?._DiscountId;
          const line = { id, quantity };
          if (discountId) line.properties = { _DiscountId: discountId };
          items.push(line);
          return;
        }
        if (this._shouldAddGiftInsideBundle(item, addonLineProps)) {
          const giftDiscountProps = { ...addonLineProps };
          delete giftDiscountProps._BundleGroup;
          delete giftDiscountProps._BundleDiscountScaleQty;
          items.push({
            id,
            quantity,
            properties: {
              ...giftDiscountProps,
              ...bundleGiftLineProperties(instanceId, item.slot)
            }
          });
          return;
        }
        const giftProps = bundleGiftLineProperties(instanceId, item.slot);
        if (addonLineProps?._BundleGroup) {
          giftProps._BundleGroup = addonLineProps._BundleGroup;
        }
        items.push({
          id,
          quantity,
          properties: giftProps
        });
      });
    }

    _resetBundleBuilderState() {
      this.selections = [];
      this.presetSelections = {};
      this._fillSingleProductSelection();
      this.drawerQuantities = {};
      this._drawerLastTotal = 0;
      this._colorsDrawerLastCount = 0;
      this._activeColorsSlot = null;
      this._activePickerContext = null;
      this._activeGiftPickerSlot = null;

      this._clearCelebration(this._getRecipeCelebrationContext());
      this._clearCelebration(this._getColorsCelebrationContext());
      this._clearMissingHighlights();

      if (this.drawer?.open) this.drawer.hide?.();
      this._closeColorsDrawer();
      this._closeGiftModal();

      this._initGiftSelections();
      this._renderSlots();
      this._updateKitRowPricing();
      this._updateKitRowSwitchability();
      this._updateDrawerPricing();
      this._syncDrawerQtyDisplay();
      this._updateUi();
    }

    _syncSelectionsBeforeAddToCart() {
      if (this._getDrawerTotalQty() > 0) {
        this._applyDrawerQuantitiesToSelections({ syncEmpty: false });
        this._renderSlots();
      }
    }

    async _addDirectOfferToCart() {
      const variantId = parseInt(
        this.querySelector('[data-bundle-kit-row][data-direct-offer].is-selected')?.getAttribute(
          'data-variant-id'
        ),
        10
      );
      if (!variantId) {
        alert('Bitte wähle zuerst ein Angebot aus.');
        return;
      }

      const cartDrawerElement = document.querySelector('cart-drawer');
      const sectionId = cartDrawerElement?.getAttribute('data-section-id') || 'cart-drawer';
      let sectionsToBundle = ['variant-added'];
      let checkoutRedirectPending = false;

      try {
        this.atcButton?.setAttribute('aria-busy', 'true');
        this.atcButton.disabled = true;

        if (!this.redirectToCheckout) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', {
              bubbles: true,
              detail: { sections: sectionsToBundle }
            })
          );
          if (!sectionsToBundle.includes(sectionId)) {
            sectionsToBundle.push(sectionId);
          }
        }

        const addPayload = { items: [{ id: variantId, quantity: 1 }] };
        if (!this.redirectToCheckout) {
          addPayload.sections = sectionsToBundle.join(',');
        }

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(addPayload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        const responseJson = await response.json();
        this._triggerCelebration(this._getAtcCelebrationContext());

        if (this.redirectToCheckout) {
          checkoutRedirectPending = true;
          setTimeout(() => {
            window.location.href = `${window.Shopify.routes.root}checkout`;
          }, 500);
          return;
        }

        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        if (!cartContent.sections) cartContent.sections = {};
        Object.assign(cartContent.sections, responseJson.sections || {});
        if (window.BkMediaBundleCartPricing?.attachFreshDrawerSection) {
          await window.BkMediaBundleCartPricing.attachFreshDrawerSection(cartContent);
        }

        const productForm = this.form || document.querySelector('form[is="product-form"]');
        const formToUse = productForm || document.createElement('form');
        let formAdded = false;
        if (!productForm) {
          document.body.appendChild(formToUse);
          formAdded = true;
        }

        formToUse.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            cancelable: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          })
        );

        if (formAdded) document.body.removeChild(formToUse);

        requestAnimationFrame(() => {
          this._resetBundleBuilderState();
        });
      } catch (error) {
        console.error('Direct offer add to cart:', error);
        alert(error.message || 'Produkt konnte nicht hinzugefügt werden.');
      } finally {
        if (!checkoutRedirectPending) {
          this.atcButton?.removeAttribute('aria-busy');
          if (this.atcButton) this.atcButton.disabled = false;
          this._updateUi();
        }
      }
    }

    async _addToCart() {
      if (this._isDirectOfferSelected()) {
        await this._addDirectOfferToCart();
        return;
      }

      this._syncSelectionsBeforeAddToCart();

      if (!this._isComplete()) {
        this._showMissingSelectionHint();
        return;
      }

      const mainVariantId = this._getSelectedKitVariantId();
      if (!mainVariantId && !this.omitParentFromCart) {
        alert('Bitte wähle zuerst ein Kit aus.');
        return;
      }

      const instanceId = generateBundleInstanceId();
      const bundleGroup = this._getEffectiveBundleGroup();
      const bundleId = generateBundleId(bundleGroup, instanceId);
      const kitRow = this._getSelectedKitRow();
      const preset = this._getPresetForRow(kitRow);
      const discountScaleQty = this._getDiscountScaleQuantity();
      const lineProps = bundleLineProperties(
        bundleId,
        mainVariantId,
        this.bundleTitle,
        bundleGroup,
        instanceId
      );
      lineProps._BundleDiscountScaleQty = String(discountScaleQty);

      const cartPricing = this._getAuthoritativeCartPricing(kitRow, preset);
      const authoritativePricing = this._resolveAuthoritativeListCompare(cartPricing, preset);
      const authoritativeListCompare = authoritativePricing.compare;
      const authoritativeListFinal = authoritativePricing.final;
      if (authoritativeListCompare > 0) {
        lineProps._BundleListCompareCents = String(authoritativeListCompare);
      }
      if (authoritativePricing.customCompare > 0) {
        lineProps._BundleCustomCompareCents = String(authoritativePricing.customCompare);
      }
      if (authoritativeListFinal > 0) {
        lineProps._BundleListFinalCents = String(authoritativeListFinal);
      }

      const addonLineProps = bundleAddonLineProperties(
        bundleId,
        bundleGroup,
        discountScaleQty,
        instanceId,
        this.bundleTitle
      );
      if (authoritativeListCompare > 0) {
        addonLineProps._BundleListCompareCents = String(authoritativeListCompare);
      }
      if (authoritativePricing.customCompare > 0) {
        addonLineProps._BundleCustomCompareCents = String(authoritativePricing.customCompare);
      }
      if (authoritativeListFinal > 0) {
        addonLineProps._BundleListFinalCents = String(authoritativeListFinal);
      }

      const items = [];
      if (!this.omitParentFromCart) {
        items.push({ id: mainVariantId, quantity: 1, properties: lineProps });
      }

      const grouped = {};
      this._sanitizeSelections(this.selections).forEach((sel) => {
        const variantId = parseInt(sel.variantId, 10);
        if (!variantId || (mainVariantId && variantId === mainVariantId)) return;
        grouped[variantId] = (grouped[variantId] || 0) + 1;
      });
      Object.entries(grouped).forEach(([id, quantity]) => {
        items.push({
          id: parseInt(id, 10),
          quantity,
          properties: { ...addonLineProps }
        });
      });

      if (this.omitParentFromCart) {
        if (!items.length) {
          alert('Bitte wähle mindestens ein Produkt aus.');
          return;
        }
        items[0].properties = {
          ...items[0].properties,
          ...lineProps
        };
      }

      this._appendGiftCartItems(items, instanceId, addonLineProps);

      if (items.length === 0) {
        alert('Bitte wähle mindestens ein Produkt aus.');
        return;
      }

      const cartDrawerElement = document.querySelector('cart-drawer');
      const sectionId = cartDrawerElement?.getAttribute('data-section-id') || 'cart-drawer';
      let sectionsToBundle = [];
      if (!this.redirectToCheckout) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );
        if (!sectionsToBundle.includes(sectionId)) {
          sectionsToBundle.push(sectionId);
        }
      }

      let checkoutRedirectPending = false;

      try {
        this.atcButton?.setAttribute('aria-busy', 'true');
        this.atcButton.disabled = true;

        const addPayload = { items };
        if (!this.redirectToCheckout) {
          addPayload.sections = sectionsToBundle.join(',');
        }

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(addPayload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        const responseJson = await response.json();

        this._triggerCelebration(this._getAtcCelebrationContext());

        if (this.redirectToCheckout) {
          checkoutRedirectPending = true;
          setTimeout(() => {
            window.location.href = `${window.Shopify.routes.root}checkout`;
          }, 500);
          return;
        }

        const addedItems = responseJson.items || [responseJson];
        let cartContent;
        try {
          cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        } catch (cartError) {
          cartContent = {
            item_count: addedItems.length,
            items: addedItems,
            cart_level_discount_applications: []
          };
        }
        cartContent.sections = responseJson.sections || {};
        if (window.BkMediaBundleCartPricing?.attachFreshDrawerSection) {
          await window.BkMediaBundleCartPricing.attachFreshDrawerSection(cartContent);
        }

        const compareToPersist =
          authoritativeListCompare || authoritativePricing.customCompare || 0;
        const shouldPersistPricing =
          Boolean(window.BkMediaBundleCartPricing?.persist) &&
          compareToPersist > authoritativeListFinal &&
          authoritativeListFinal > 0;

        const productForm = this.form || document.querySelector('form[is="product-form"]');
        const formToUse = productForm || document.createElement('form');
        let formAdded = false;
        if (!productForm) {
          document.body.appendChild(formToUse);
          formAdded = true;
        }

        formToUse.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            cancelable: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          })
        );

        if (formAdded) document.body.removeChild(formToUse);

        requestAnimationFrame(() => {
          this._resetBundleBuilderState();
        });

        if (shouldPersistPricing) {
          window.BkMediaBundleCartPricing.persist({
            bundleId,
            instanceId,
            compareCents: compareToPersist,
            finalCents: authoritativeListFinal,
            cart: cartContent
          }).catch(() => {});
        }
      } catch (error) {
        console.error('Bundle builder add to cart:', error);
        alert(error.message || 'Produkte konnten nicht hinzugefügt werden.');
      } finally {
        if (!checkoutRedirectPending) {
          this.atcButton?.removeAttribute('aria-busy');
          if (this.atcButton) this.atcButton.disabled = false;
          this._updateUi();
        }
      }
    }
  }

  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('bundle-builder').forEach((el) => {
      if (typeof el.connectedCallback === 'function') el.connectedCallback();
    });
  });
})();
